// Mind2Magic CRM — PDF Import Module
// Extracted from index.html Step 9.

import { store } from '../store.js'
import { STAGES, TYPE_LABELS } from '../constants.js'

// ========== PDF IMPORTER ==========
let pdfExtractedData = null

// Variable to hold pending Excel row for duplicate handling
let pendingExcelRow = null

async function handlePDFFile(event) {
    const file = event.target.files[0]
    if (!file || !file.name.endsWith('.pdf')) {
        window.toast('Por favor selecciona un archivo PDF', 'error')
        return
    }

    window.toast('📄 Procesando PDF...', 'success')

    try {
        const arrayBuffer = await file.arrayBuffer()
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

        let fullText = ''
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i)
            const textContent = await page.getTextContent()

            // Position-aware text extraction: group items by line (y-position),
            // sort left-to-right within each line. Preserves reading order for
            // table layouts and multi-column email PDFs (critical for Gmail → PDF).
            const items = textContent.items.filter(item => item.str.trim())
            if (items.length === 0) { fullText += '\n'; continue }

            // Sort by y descending (top of page first), then x ascending (left to right)
            items.sort((a, b) => {
                const yDiff = b.transform[5] - a.transform[5]
                if (Math.abs(yDiff) > 3) return yDiff
                return a.transform[4] - b.transform[4]
            })

            // Group into lines: items within 3pt vertically = same line
            const lines = []
            let currentLine = null
            for (const item of items) {
                const y = item.transform[5]
                if (!currentLine || Math.abs(currentLine.y - y) > 3) {
                    currentLine = { y, items: [] }
                    lines.push(currentLine)
                }
                currentLine.items.push({ x: item.transform[4], text: item.str })
            }

            for (const line of lines) {
                line.items.sort((a, b) => a.x - b.x)
                fullText += line.items.map(it => it.text).join(' ') + '\n'
            }
            fullText += '\n'
        }

        console.log('PDF Text extracted (full):', fullText)

        // Detectar operador y parsear
        const data = parsePDFContent(fullText)

        if (data) {
            // Extract client email from Gmail "To:" header (first 1000 chars only)
            // Skip known agent/agency emails so Travel Advisor Copy doesn't set Sara's email on client
            if (!data.email) {
                const headerArea = fullText.substring(0, 1000)
                const toMatch = headerArea.match(/^To:\s*([\w.+-]+@[\w.-]+\.\w+)/m)
                if (toMatch) {
                    const email = toMatch[1].toLowerCase()
                    const agentEmails = ['saramava@gmail.com', 'sara@m2magic.com', 'info@m2magic.com']
                    if (!agentEmails.includes(email)) data.email = email
                }
            }

            // Extract reservation# from filename as fallback
            // Formats: "[KAMLE FAJARDO _ 44138522].pdf" or "[KAMLE FAJARDO _ 44138522 _ Travel Advisor Copy].pdf"
            const fnMatch = file.name.match(/\[.+?[\s_|]+(\d{6,})(?:[\s_|]|\])/)
            if (fnMatch && !data.reservationNum) {
                data.reservationNum = fnMatch[1].trim()
            }

            // Extract name from filename for TAAP PDFs as fallback
            // Format: "Angelica Franco - TAAP Trip Details - 73377882176614.pdf"
            // or "Andres Sanchez - TAAP Trip Details - 73377860069027-2.pdf"
            if (!data.titular && data.operatorType === 'expedia') {
                const nameFromFile = file.name.match(/^([A-Za-zÁÉÍÓÚÑáéíóúñ]+(?:\s+[A-Za-zÁÉÍÓÚÑáéíóúñ]+)+)\s*-/i)
                if (nameFromFile) data.titular = window.normalizeName(nameFromFile[1].trim())
            }

            pdfExtractedData = data
            showPDFPreview(data)
        } else {
            window.toast('No se pudo identificar el formato del PDF', 'error')
        }
    } catch (error) {
        console.error('Error processing PDF:', error)
        window.toast('Error al procesar el PDF: ' + error.message, 'error')
    }

    // Reset file input
    event.target.value = ''
}

function parsePDFContent(text) {
    // Detectar operador
    const textUpper = text.toUpperCase()

    // Expedia TAAP — FIRST because TAAP PDFs can contain keywords that match
    // other parsers: "Disney MagicMobile" → "DISNEY MAGIC" (DCL ship), or
    // "Walt Disney World" (Parks). TAAP markers are unique and safe to check first.
    if (textUpper.includes('EXPEDIATAAP') || textUpper.includes('EXPEDIA TAAP') ||
        textUpper.includes('TAAP ITINERARY') ||
        (textUpper.includes('ITINERARY NUMBER:') && textUpper.includes('EXPEDIA')) ||
        (textUpper.includes('NÚMERO DE ITINERARIO:') && textUpper.includes('EXPEDIA'))) {
        return parseExpediaTAAP(text)
    }

    if (textUpper.includes('MSC CRUISES') || textUpper.includes('MSC WORLD') || textUpper.includes('MSC GRANDIOSA') || textUpper.includes('EMBARKATION FORM')) {
        return parseMSCCruises(text)
    }

    // Disney Cruise Line — check BEFORE Disney Parks because cruise PDFs can mention
    // "Walt Disney World" (port adventures, combo packages) which would false-match parks
    if (textUpper.includes('DISNEY FANTASY') || textUpper.includes('DISNEY DREAM') || textUpper.includes('DISNEY WISH') || textUpper.includes('DISNEY TREASURE') || textUpper.includes('DISNEY MAGIC') || textUpper.includes('DISNEY CRUISE') || textUpper.includes('DISNEYCRUISE.DISNEY.GO.COM') || textUpper.includes('BIBBIDI') || textUpper.includes('EMBARK DATE') || textUpper.includes('STATEROOM')) {
        return parseDisneyCruise(text)
    }

    // Disney Parks (Hotel + Tickets packages) — only after cruise + TAAP are ruled out
    if (textUpper.includes('PACKAGE CONFIRMATION NUMBER') || textUpper.includes('WALT DISNEY WORLD') ||
        textUpper.includes("DISNEY'S PORT ORLEANS") || textUpper.includes("DISNEY'S CORONADO") ||
        textUpper.includes("DISNEY'S CARIBBEAN") || textUpper.includes("DISNEY'S ANIMAL KINGDOM LODGE") ||
        textUpper.includes("DISNEY'S GRAND FLORIDIAN") || textUpper.includes("DISNEY'S CONTEMPORARY") ||
        textUpper.includes("DISNEY'S POLYNESIAN") || textUpper.includes("DISNEY'S WILDERNESS") ||
        textUpper.includes("DISNEY'S YACHT") || textUpper.includes("DISNEY'S BEACH CLUB") ||
        textUpper.includes("DISNEY'S BOARDWALK") || textUpper.includes("DISNEY'S RIVIERA") ||
        textUpper.includes("DISNEY'S ART OF ANIMATION") || textUpper.includes("DISNEY'S POP CENTURY") ||
        textUpper.includes("DISNEY'S ALL-STAR") || textUpper.includes("RESERVATION IS ON HOLD")) {
        return parseDisneyParks(text)
    }

    // Universal Orlando / VAX — check before Royal Caribbean
    if (textUpper.includes('UNIVERSAL PARKS AND RESORTS') || textUpper.includes('UNIVERSALORLANDOVACATIONS') ||
        textUpper.includes('MAINSTREET TRAVEL AGENCY') || textUpper.includes('UNIVERSAL CABANA BAY') ||
        textUpper.includes('UNIVERSAL AVENTURA') || textUpper.includes('UNIVERSAL ENDLESS SUMMER') ||
        textUpper.includes('UNIVERSAL DOCKSIDE') || textUpper.includes('LOEWS PORTOFINO') ||
        textUpper.includes('LOEWS ROYAL PACIFIC') || textUpper.includes('HARD ROCK HOTEL') ||
        (textUpper.includes('UNIVERSAL') && textUpper.includes('RESERVATION NUMBER'))) {
        return parseUniversalVAX(text)
    }

    if (textUpper.includes('ROYAL CARIBBEAN')) {
        return parseRoyalCaribbean(text)
    }

    return null
}

// ========== DISNEY PARKS PARSER ==========
function parseDisneyParks(text) {
    const { settings } = store

    const data = {
        operator: 'Disney',
        operatorType: 'disneyparks',
        type: 'disney',
        tipoParque: 'Disney World'
    }

    // Package Confirmation Number
    const confMatch = text.match(/Package\s*Confirmation\s*Number\s*[:\s]*(\d+)/i)
    data.reservationNum = confMatch ? confMatch[1] : null

    // Hotel/Resort Name - stop at newline or specific keywords
    const hotelMatch = text.match(/Disney'?s\s+([A-Za-z\s\-]+(?:Resort|Lodge|Inn|Hotel))(?:\s*-\s*([A-Za-z]+))?/i)
    if (hotelMatch) {
        data.hotel = "Disney's " + hotelMatch[1].trim()
        if (hotelMatch[2]) {
            data.hotel += ' - ' + hotelMatch[2].trim()
        }
    }

    // Dates - multiple formats:
    // Format 1: "Saturday, June 6, 2026 - Friday, June 12, 2026" (confirmation)
    // Format 2: "June 6 – June 12, 2026" (cart)
    const months = {
        'january': '01', 'february': '02', 'march': '03', 'april': '04',
        'may': '05', 'june': '06', 'july': '07', 'august': '08',
        'september': '09', 'october': '10', 'november': '11', 'december': '12',
        'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04',
        'jun': '06', 'jul': '07', 'aug': '08', 'sep': '09',
        'oct': '10', 'nov': '11', 'dec': '12'
    }

    // Try confirmation format first
    let datesMatch = text.match(/(\w+day,?\s+(\w+)\s+(\d{1,2}),?\s+(\d{4}))\s*[-–]\s*(\w+day,?\s+(\w+)\s+(\d{1,2}),?\s+(\d{4}))/i)
    if (datesMatch) {
        const startMonth = datesMatch[2]
        const startDay = parseInt(datesMatch[3])
        const startYear = parseInt(datesMatch[4])
        const endMonth = datesMatch[6]
        const endDay = parseInt(datesMatch[7])
        const endYear = parseInt(datesMatch[8])

        const startMonthNum = months[startMonth.toLowerCase()] || '01'
        const endMonthNum = months[endMonth.toLowerCase()] || '01'

        data.travelDate = `${startYear}-${startMonthNum}-${String(startDay).padStart(2, '0')}`
        data.travelEnd = `${endYear}-${endMonthNum}-${String(endDay).padStart(2, '0')}`
    } else {
        // Try cart format: "June 6 – June 12, 2026" or "June 6 - June 12, 2026"
        const cartDatesMatch = text.match(/(\w+)\s+(\d{1,2})\s*[-–]\s*(\w+)\s+(\d{1,2}),?\s+(\d{4})/i)
        if (cartDatesMatch) {
            const startMonth = cartDatesMatch[1]
            const startDay = parseInt(cartDatesMatch[2])
            const endMonth = cartDatesMatch[3]
            const endDay = parseInt(cartDatesMatch[4])
            const year = parseInt(cartDatesMatch[5])

            const startMonthNum = months[startMonth.toLowerCase()] || '01'
            const endMonthNum = months[endMonth.toLowerCase()] || '01'

            data.travelDate = `${year}-${startMonthNum}-${String(startDay).padStart(2, '0')}`
            data.travelEnd = `${year}-${endMonthNum}-${String(endDay).padStart(2, '0')}`
        }
    }

    // Room Type - "Royal Guest Room" but stop before numbers or dates
    const roomMatch = text.match(/\n([A-Za-z\s]+Room)\n/i) ||
                      text.match(/([A-Za-z]+\s+(?:Guest\s+)?Room)(?:\s*\n|\s+\d)/i)
    if (roomMatch) {
        data.roomType = roomMatch[1].trim()
    }

    // Guests count - "2 Adults, 2 Children (Infant, 7)" or "3 Adults, 1 Child (4)"
    const guestsMatch = text.match(/(\d+)\s*Adults?,\s*(\d+)\s*Child(?:ren?)?\s*\(([^)]+)\)/i)
    if (guestsMatch) {
        data.adultsCount = parseInt(guestsMatch[1])
        data.childrenCount = parseInt(guestsMatch[2])
        // Parse ages: could be "4" or "Infant, 7" or "3, 7, Infant"
        const agesRaw = guestsMatch[3].split(',').map(s => s.trim())
        data.childAges = agesRaw.map(a => /^\d+$/.test(a) ? a : a.toLowerCase() === 'infant' ? '1' : a)
        data.childAge = data.childAges.join(', ')
    } else {
        const adultsOnly = text.match(/(\d+)\s*Adults?/i)
        const childOnly = text.match(/(\d+)\s*Child/i)
        if (adultsOnly) data.adultsCount = parseInt(adultsOnly[1])
        if (childOnly) data.childrenCount = parseInt(childOnly[1])
    }

    // Tickets - "6-Day Ticket with Admission to 1 Park Per Day"
    const ticketsMatch = text.match(/(\d+)[- ]Day\s+Ticket(?:\s+with\s+Admission\s+to\s+(\d+)\s+Park(?:s)?\s+Per\s+Day)?/i)
    if (ticketsMatch) {
        data.tickets = ticketsMatch[0].trim()
        data.diasParque = parseInt(ticketsMatch[1])
        data.parkHopper = ticketsMatch[2] && parseInt(ticketsMatch[2]) > 1
    }

    // Ticket validity dates - "Valid any 6 days from Sat, Jun 6, 2026 through Sun, Jun 14, 2026"
    const validityMatch = text.match(/Valid\s+any\s+\d+\s+days?\s+from\s+\w+,?\s+(\w+)\s+(\d{1,2}),?\s+(\d{4})\s+through\s+\w+,?\s+(\w+)\s+(\d{1,2}),?\s+(\d{4})/i)
    if (validityMatch) {
        const months = {
            'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04', 'may': '05', 'jun': '06',
            'jul': '07', 'aug': '08', 'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12',
            'january': '01', 'february': '02', 'march': '03', 'april': '04',
            'june': '06', 'july': '07', 'august': '08', 'september': '09',
            'october': '10', 'november': '11', 'december': '12'
        }
        const startM = months[validityMatch[1].toLowerCase()] || '01'
        const startD = String(parseInt(validityMatch[2])).padStart(2, '0')
        const startY = validityMatch[3]
        const endM = months[validityMatch[4].toLowerCase()] || '01'
        const endD = String(parseInt(validityMatch[5])).padStart(2, '0')
        const endY = validityMatch[6]

        data.ticketValidFrom = `${startY}-${startM}-${startD}`
        data.ticketValidTo = `${endY}-${endM}-${endD}`
    }

    // Disney Dining Plan
    data.hasDiningPlan = /Disney\s*Dining\s*Plan/i.test(text)

    // Travel Party - find all names with titles (Ms., Mr., Miss, Mrs.) in entire document
    // Miss typically indicates a child/minor
    data.guests = []
    data.guestsDetailed = []
    const nameRegex = /(Ms\.?|Mr\.?|Miss|Mrs\.?|Mstr\.?|Master)\s+([A-Za-záéíóúñÁÉÍÓÚÑ]+\s+[A-Za-záéíóúñÁÉÍÓÚÑ]+)/g
    let match
    let minorCount = 0
    while ((match = nameRegex.exec(text)) !== null) {
        const title = match[1]
        const name = window.normalizeName(match[2])
        // Filter out false positives
        if (name.length > 3 && !name.toLowerCase().includes('room') && !name.toLowerCase().includes('amenity')) {
            data.guests.push(name)
            const titleLower = title.toLowerCase().replace('.', '')
            const isMiss = titleLower === 'miss'
            const isMstr = titleLower === 'mstr' || titleLower === 'master'
            data.guestsDetailed.push({
                nombre: name,
                titulo: title,
                esMenor: isMiss || isMstr
            })
            if (isMiss || isMstr) minorCount++
        }
    }

    // Assign ages to minors from childAges array
    if (minorCount > 0) {
        const minors = data.guestsDetailed.filter(g => g.esMenor)
        if (data.childAges && data.childAges.length > 0) {
            // Match ages to minors in order
            for (let i = 0; i < Math.min(minors.length, data.childAges.length); i++) {
                minors[i].edad = data.childAges[i]
            }
        } else if (data.childAge && minors.length === 1) {
            minors[0].edad = data.childAge
        }
    }

    // Package Price - handle multiple formats
    // Confirmation: "Package Price $6,421.81" or "Total Price $2,937.23"
    // Cart: "Subtotal $4,316.29 USD" or "$ 4,316.29"
    const pricePatterns = [
        /Package\s*Price[\s\S]*?\$\s*([\d,]+\.?\d*)/i,
        /Package\s*Price\s*\$?([\d,]+\.?\d*)/i,
        /Total\s*Order\s*Price[\s\S]*?\$\s*([\d,]+\.?\d*)/i,
        /Total\s*Payment\s*Due\s*\$\s*([\d,]+\.?\d*)/i,
        /Total\s*Price\s*\$\s*([\d,]+\.?\d*)/i,
        /Subtotal\s*\$\s*([\d,]+\.?\d*)\s*USD/i,
        /Offer\s*Subtotal\s*\$?\s*([\d,]+\.?\d*)/i,
        /\$\s*([\d,]+\.\d{2})\s*USD/i
    ]
    for (const pattern of pricePatterns) {
        const match = text.match(pattern)
        if (match) {
            const price = parseFloat(match[1].replace(/,/g, ''))
            if (price > 0) {
                data.totalVenta = price
                break
            }
        }
    }
    if (!data.totalVenta) data.totalVenta = 0

    // Deposit amount — "Deposit $200", "Payment Today -$200", "$200 was charged to your Visa"
    const depositMatch = text.match(/Deposit\s*\$?([\d,]+\.?\d*)/i)
        || text.match(/Payment\s*Today\s*-?\$\s*([\d,]+\.?\d*)/i)
        || text.match(/\$\s*([\d,]+\.?\d*)\s*was\s*charged/i)
    data.depositAmount = depositMatch ? parseFloat(depositMatch[1].replace(/,/g, '')) : 0

    // Deposit due date - "Due on Jan 24, 2026"
    const depositDueMatch = text.match(/Deposit[\s\S]*?Due\s*(?:on)?\s*(\w+)\s+(\d{1,2}),?\s+(\d{4})/i)
    if (depositDueMatch) {
        const months = {
            'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04', 'may': '05', 'jun': '06',
            'jul': '07', 'aug': '08', 'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12',
            'january': '01', 'february': '02', 'march': '03', 'april': '04',
            'june': '06', 'july': '07', 'august': '08', 'september': '09',
            'october': '10', 'november': '11', 'december': '12'
        }
        const m = months[depositDueMatch[1].toLowerCase()] || '01'
        const d = String(parseInt(depositDueMatch[2])).padStart(2, '0')
        const y = depositDueMatch[3]
        data.fechaAbono = `${y}-${m}-${d}`
    }

    // Remaining Balance — "Remaining Balance Due $X" or plain "Balance Due $X"
    const balanceMatch = text.match(/Remaining\s*Balance\s*(?:Due)?\s*\$?([\d,]+\.?\d*)/i)
        || text.match(/Balance\s*Due\s*\$\s*([\d,]+\.?\d*)/i)
    data.balanceDue = balanceMatch ? parseFloat(balanceMatch[1].replace(/,/g, '')) : null

    // Balance due date - "Due on May 7, 2026" (after "Remaining Balance" or standalone)
    const balanceDueMatch = text.match(/Remaining\s*Balance[\s\S]*?Due\s*(?:on)?\s*(\w+)\s+(\d{1,2}),?\s+(\d{4})/i)
        || text.match(/Balance\s*Due[\s\S]*?Due\s*(?:on)?\s*(\w+)\s+(\d{1,2}),?\s+(\d{4})/i)
    if (balanceDueMatch) {
        const months = {
            'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04', 'may': '05', 'jun': '06',
            'jul': '07', 'aug': '08', 'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12',
            'january': '01', 'february': '02', 'march': '03', 'april': '04',
            'june': '06', 'july': '07', 'august': '08', 'september': '09',
            'october': '10', 'november': '11', 'december': '12'
        }
        const m = months[balanceDueMatch[1].toLowerCase()] || '01'
        const d = String(parseInt(balanceDueMatch[2])).padStart(2, '0')
        const y = balanceDueMatch[3]
        data.fechaPagoFinal = `${y}-${m}-${d}`
    }

    // Commission — use actual from PDF if available, otherwise estimate 10%
    const commMatch = text.match(/Commission\s*\$\s*([\d,]+\.?\d*)/i)
    if (commMatch) {
        data.commission = parseFloat(commMatch[1].replace(/,/g, ''))
        data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))
    } else if (data.totalVenta && data.totalVenta > 0) {
        data.commission = parseFloat((data.totalVenta * 0.10).toFixed(2))
        data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))
    } else {
        data.commission = 0
        data.commission70 = 0
    }

    // Fallback: if we have deposit + total but balance regex missed (column-order table extraction), calculate it
    if (data.balanceDue === null && data.depositAmount > 0 && data.totalVenta > 0) {
        data.balanceDue = data.totalVenta - data.depositAmount
    }

    // Determine payment status
    const textLower = text.toLowerCase()

    // Confirmed reservation with "was charged" = deposit was paid
    const isConfirmed = textLower.includes('reservation is confirmed') || textLower.includes('reservation was successfully')

    // Check if this is a cart (quote) vs a confirmed reservation
    const isCart = textLower.includes('your cart') || (!data.reservationNum && textLower.includes('subtotal'))

    if (isCart) {
        // It's a quote/cart, not a confirmed reservation
        data.paymentStatus = 'quote'
        data.stage = 'cotizacion'
        data.abonado = 0
        data.abono = data.totalVenta ? parseFloat((data.totalVenta * 0.05).toFixed(2)) : 200 // Estimate 5% deposit
    } else if (textLower.includes('hold applied') || textLower.includes('reservation is on hold')) {
        data.paymentStatus = 'hold'
        data.stage = 'prereserva'
        data.abonado = 0
        data.abono = data.depositAmount || 200
    } else if (textLower.includes('paid in full') || textLower.includes('payment complete') ||
               (data.balanceDue !== null && data.balanceDue === 0)) {
        data.paymentStatus = 'paid'
        data.stage = 'pago_completo'
        data.abonado = data.totalVenta || 0
        data.abono = data.totalVenta || 0
    } else if (data.depositAmount > 0 && (data.balanceDue > 0 || isConfirmed)) {
        data.paymentStatus = 'partial'
        data.stage = 'reserva_abono'
        data.abonado = data.depositAmount
        data.abono = data.depositAmount
        if (data.balanceDue === null) data.balanceDue = (data.totalVenta || 0) - data.depositAmount
    } else {
        data.paymentStatus = 'pending'
        data.stage = 'prereserva'
        data.abonado = 0
        data.abono = 200
    }

    // Primary guest (first in travel party)
    if (data.guests && data.guests.length > 0) {
        data.titular = window.normalizeName(data.guests[0])
    }

    // Destination
    data.destination = data.hotel || 'Walt Disney World'

    // Viajeros string with age
    if (data.adultsCount) {
        data.viajeros = `${data.adultsCount}A`
        if (data.childrenCount) {
            data.viajeros += `, ${data.childrenCount}M`
            if (data.childAge) {
                data.viajeros += `(${data.childAge})`
            }
        }
    }

    // Detalle del paquete (simplificado)
    let detalles = []
    if (data.tickets) {
        // Simplificar el texto del ticket
        const ticketSimple = data.tickets.match(/(\d+)[- ]Day\s+Ticket/i)
        detalles.push(ticketSimple ? `${ticketSimple[1]}-Day Ticket` : data.tickets)
    }
    if (data.hasDiningPlan) detalles.push('Disney Dining Plan')
    data.detalle = detalles.join(' + ') || null

    return data
}

// ========== MSC CRUISES PARSER ==========
function parseMSCCruises(text) {
    const data = {
        operator: 'MSC Cruises',
        operatorType: 'msc',
        type: 'crucero'
    }

    // Booking Number — "Booking number : 70853933" or "BOOKING NUMBER: 70853933"
    const bookingMatch = text.match(/Booking\s*number\s*[:\s]*(\d+)/i)
    data.reservationNum = bookingMatch ? bookingMatch[1] : null

    // Ship — explicit list first (most reliable), then generic "Ship:" pattern (captures multi-word names)
    const shipMatch = text.match(/(MSC\s+(?:World Europa|Grandiosa|Meraviglia|Seaside|Seashore|Seascape|Bellissima|Virtuosa|Euribia|Divina|Magnifica|Poesia|Sinfonia|Armonia|Lirica|Opera|Musica|Splendida|Fantasia|Preziosa))/i) ||
                      text.match(/Ship\s*[:\s]*(MSC\s+[\w]+(?:\s+[\w]+)?)/i)
    data.ship = shipMatch ? shipMatch[1].trim() : null

    // Cabin number — "Cabin: 9191" or "Cabin: 9191 − Booked"
    const cabinMatch = text.match(/Cabin\s*[:\s]*(\d{3,5})/i)
    data.cabin = cabinMatch ? cabinMatch[1] : null

    // Cabin Type — "Category: BR1 - Balcony Stateroom" or "English Info Cabin Deluxe Balcony" or "Cabin Type: ..."
    const cabinTypeMatch = text.match(/Category\s*[:\s]*[\w\d]+\s*-\s*([\w\s]+?)(?:\s+Cabin|\n|$)/im) ||
                           text.match(/English\s*Info\s*Cabin\s+([\w\s]+?)(?:\n|Cabin\s*Area)/i) ||
                           text.match(/Cabin\s*Type\s*[:\s]*([\w\s]+?)(?:\s+Experience|\s+Assembly|\n|$)/im)
    data.cabinType = cabinTypeMatch ? cabinTypeMatch[1].trim() : null

    // Experience — "Your experience : FANTASTICA" or "Experience: ..."
    const expMatch = text.match(/experience\s*[:\s]*([\w]+)/i)
    data.experience = expMatch ? expMatch[1].trim() : null

    // Embarkation Date (MM/DD/YYYY)
    const dateMatch = text.match(/Embarkation\s*Date\s*[:\s]*(\d{1,2}\/\d{1,2}\/\d{4})/i)
    if (dateMatch) {
        const [month, day, year] = dateMatch[1].split('/')
        data.travelDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }

    // Disembarkation Date — "Disembarkation Date: 04/13/2026"
    const disembarkMatch = text.match(/Disembarkation\s*Date\s*[:\s]*(\d{1,2}\/\d{1,2}\/\d{4})/i)
    if (disembarkMatch) {
        const [month, day, year] = disembarkMatch[1].split('/')
        data.travelEnd = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }

    // Embarkation Port — "Embarkation port: Miami, Florida" or inline "Port: Miami, Florida"
    // Try explicit "Embarkation port:" first, then "Port:" from embarkation date line context
    const portMatch = text.match(/Embarkation\s+port\s*:\s*([A-Za-z\s,]+?)(?:\s+Your|\s+Guest|\s+Booking|\n)/i) ||
                      text.match(/Embarkation\s+Date\s*:.*?Port\s*:\s*([A-Za-z\s,]+?)(?:\s+Your|\s+Guest|\s+Booking|\n)/i)
    data.embarkationPort = portMatch ? portMatch[1].trim().replace(/,\s*$/, '') : null

    // Booking Status
    const statusMatch = text.match(/Booking\s*Status\s*[:\s]*([\w]+)/i)
    data.status = statusMatch ? statusMatch[1].trim().toLowerCase() : null

    // Option valid until (for holds)
    const optionMatch = text.match(/Option\s*valid\s*until.*?:\s*([\w\s,]+\d{4})/i)
    data.optionDeadline = optionMatch ? optionMatch[1].trim() : null

    // ===== GUESTS =====
    data.guests = []
    data.guestsDetailed = []

    // Format 1: Booking confirmation — "GUTIERREZ JURI, CAROLINA (DOB : 08/04/1982)"
    const dobGuests = [...text.matchAll(/([A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s]+?)\s*,\s*([A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s]+?)\s*\(\s*DOB\s*:\s*(\d{1,2}\/\d{1,2}\/\d{4})\s*\)/gi)]
    if (dobGuests.length > 0) {
        const seen = new Set()
        for (const m of dobGuests) {
            const lastName = m[1].trim()
            const firstName = m[2].trim()
            const dob = m[3]
            const key = `${lastName}|${firstName}|${dob}`
            if (seen.has(key)) continue
            seen.add(key)

            const fullName = formatGuestName(`${firstName} ${lastName}`)
            data.guests.push(fullName)

            // Calculate age from DOB
            const [dobMonth, dobDay, dobYear] = dob.split('/')
            const birthDate = new Date(parseInt(dobYear), parseInt(dobMonth) - 1, parseInt(dobDay))
            const today = new Date()
            let age = today.getFullYear() - birthDate.getFullYear()
            const md = today.getMonth() - birthDate.getMonth()
            if (md < 0 || (md === 0 && today.getDate() < birthDate.getDate())) age--

            data.guestsDetailed.push({
                nombre: fullName,
                edad: age,
                esMenor: age < 18,
                esTitular: data.guestsDetailed.length === 0
            })
        }
    }

    // Format 2: Embarkation form — "First Name XXXX Last Name YYYY"
    if (data.guests.length === 0) {
        const fnMatches = text.matchAll(/First\s*Name\s+([A-ZÁÉÍÓÚÑ\s]+?)(?:\s+Last|\s+\d)/gi)
        const lnMatches = text.matchAll(/Last\s*Name\s+([A-ZÁÉÍÓÚÑ\s]+?)(?:\s+Nationality|\s+\d)/gi)
        const firstNames = [...fnMatches].map(m => m[1].trim())
        const lastNames = [...lnMatches].map(m => m[1].trim())
        for (let i = 0; i < Math.min(firstNames.length, lastNames.length); i++) {
            const fullName = formatGuestName(`${firstNames[i]} ${lastNames[i]}`)
            data.guests.push(fullName)
            data.guestsDetailed.push({ nombre: fullName, esMenor: false, esTitular: i === 0 })
        }
    }

    // Guest counts from guestsDetailed
    const adultsCount = data.guestsDetailed.filter(g => !g.esMenor).length
    const childrenCount = data.guestsDetailed.filter(g => g.esMenor).length
    if (adultsCount > 0) {
        data.adultsCount = adultsCount
        data.childrenCount = childrenCount
    }

    // ===== PRICES =====
    // Format 1: Booking confirmation — "Total Booking amount due in : USD* 1305.00"
    const totalAmountMatch = text.match(/Total\s*Booking\s*amount\s*due\s*in\s*[:\s]*USD\*?\s*([\d,]+\.?\d*)/i)
    if (totalAmountMatch) {
        data.totalVenta = parseFloat(totalAmountMatch[1].replace(',', ''))
    }

    // Format 2: "Total Amount 1305.00" in Payment Details section
    if (!data.totalVenta) {
        const paymentTotalMatch = text.match(/Total\s*Amount\s+([\d,]+\.?\d+)/i)
        if (paymentTotalMatch) {
            data.totalVenta = parseFloat(paymentTotalMatch[1].replace(',', ''))
        }
    }

    // Format 3: Old format — "Total Booking Price $XXXX"
    if (!data.totalVenta) {
        const totalMatch = text.match(/Total\s*Booking\s*Price\s*\$?\s*([\d,]+\.?\d*)/i)
        if (totalMatch) {
            data.totalVenta = parseFloat(totalMatch[1].replace(',', ''))
        }
    }

    // Commission — "Commission 141.12" in Payment Details
    const { settings } = store
    const commissionMatch = text.match(/\bCommission\s+([\d,]+\.?\d+)/i)
    if (commissionMatch) {
        data.commission = parseFloat(commissionMatch[1].replace(/,/g, ''))
    } else if (data.totalVenta && data.totalVenta > 0) {
        data.commission = parseFloat((data.totalVenta * 0.10).toFixed(2))
    } else {
        data.commission = 0
    }
    data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))

    // Paid amount — "Paid Booking to date in : USD 1305.00" or "Payments 1305.00"
    const paidMatch = text.match(/Paid\s*Booking\s*to\s*date\s*in\s*[:\s]*USD\s*([\d,]+\.?\d*)/i) ||
                      text.match(/\bPayments\s+([\d,]+\.?\d+)/i)
    if (paidMatch) {
        data.abonado = parseFloat(paidMatch[1].replace(/,/g, ''))
    }

    // Abono esperado
    data.abono = data.abonado || (settings.abonoCrucero || 500)

    // Destination program — "Program United States, Bahamas 4 Days− 3 Nights"
    const programMatch = text.match(/Program\s+([\w\s,]+?)\s+\d+\s*(?:Days?|Giorni)/i)
    if (programMatch) {
        data.program = programMatch[1].trim().replace(/,\s*$/, '')
    }

    // Duration — "4 Days− 3 Nights" or "4 Giorni− 3 Notti" or "Duration: 3 NIGHTS"
    const durationMatch = text.match(/(\d+)\s*(?:Days?|Giorni)\s*[−\-–]\s*(\d+)\s*(?:Nights?|Notti)/i) ||
                         text.match(/Duration\s*[:\s]*(\d+)\s*NIGHTS/i)
    if (durationMatch) {
        data.duration = parseInt(durationMatch[2] || durationMatch[1])
    }

    // Itinerary end date (old format fallback — embarkation form with port schedule)
    if (!data.travelEnd) {
        const itineraryMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})\s+[\w\s,]+\s+\d{1,2}:\d{2}\s+--:--/gi)
        if (itineraryMatch && itineraryMatch.length > 0) {
            const lastPort = itineraryMatch[itineraryMatch.length - 1]
            const endDateMatch = lastPort.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/)
            if (endDateMatch) {
                const parts = endDateMatch[1].split('/')
                if (parts[2].length === 2) parts[2] = '20' + parts[2]
                data.travelEnd = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`
            }
        }
    }

    // Determine payment status
    if (data.status === 'optioned') {
        data.paymentStatus = 'hold'
        data.stage = 'prereserva'
    } else if (data.abonado && data.totalVenta && data.abonado >= data.totalVenta) {
        data.paymentStatus = 'paid'
        data.stage = 'pago_completo'
    } else if (data.status === 'confirmed' || text.includes('EMBARKATION FORM')) {
        data.paymentStatus = 'paid'
        data.stage = 'pago_completo'
    } else if (data.abonado && data.abonado > 0) {
        data.paymentStatus = 'partial'
        data.stage = 'reserva_abono'
    } else {
        data.paymentStatus = 'pending'
        data.stage = 'prereserva'
    }

    // Get primary guest name
    if (data.guestsDetailed.length > 0) {
        const primary = data.guestsDetailed.find(g => !g.esMenor) || data.guestsDetailed[0]
        data.titular = primary.nombre
    } else if (data.guests.length > 0) {
        data.titular = window.normalizeName(formatGuestName(data.guests[0]))
    }

    // Destination — prefer Program field over just ship name
    if (data.program) {
        data.destination = `Crucero ${data.ship || 'MSC'} - ${data.program}`
    } else {
        data.destination = data.ship ? `Crucero ${data.ship}` : 'MSC Cruises'
    }
    if (data.embarkationPort && !data.destination.includes(data.embarkationPort)) {
        data.destination += ` - ${data.embarkationPort}`
    }

    // Build viajeros string
    if (data.adultsCount) {
        data.viajeros = `${data.adultsCount}A`
        if (data.childrenCount) {
            const childAges = data.guestsDetailed.filter(g => g.esMenor && g.edad != null).map(g => g.edad)
            data.viajeros += `, ${data.childrenCount}M`
            if (childAges.length > 0) data.viajeros += `(${childAges.join(',')})`
        }
    }

    return data
}

// ========== DISNEY CRUISE LINE PARSER ==========
function parseDisneyCruise(text) {
    const { settings } = store
    const textUpper = text.toUpperCase()

    // Detect format: email confirmation ("Bibbidi, bobbidi, booked!") vs booking cart/quote
    const isEmailConfirmation = textUpper.includes('BIBBIDI') || textUpper.includes('EMBARK DATE') || textUpper.includes('DEBARK DATE') ||
        textUpper.includes('TRAVEL ADVISOR COPY') || textUpper.includes('YOUR CLIENT\'S RESERVATION IS CONFIRMED')

    const data = {
        operator: 'Disney Cruise Line',
        operatorType: 'disney',
        type: 'crucero'
    }

    // Month abbreviation map for DD-Mon-YYYY parsing
    const monAbbr = { 'jan':'01','feb':'02','mar':'03','apr':'04','may':'05','jun':'06','jul':'07','aug':'08','sep':'09','oct':'10','nov':'11','dec':'12',
        'january':'01','february':'02','march':'03','april':'04','june':'06','july':'07','august':'08','september':'09','october':'10','november':'11','december':'12' }

    // Helper: parse "19-Jun-2026" or "Jun 19, 2026" to ISO string
    function parseDCLDate(str) {
        if (!str) return null
        // DD-Mon-YYYY
        let m = str.match(/(\d{1,2})-(\w+)-(\d{4})/)
        if (m) {
            const mon = monAbbr[m[2].toLowerCase()]
            if (mon) return `${m[3]}-${mon}-${String(m[1]).padStart(2, '0')}`
        }
        // Mon DD, YYYY or Month DD, YYYY
        m = str.match(/(\w+)\s+(\d{1,2}),?\s*(\d{4})/)
        if (m) {
            const mon = monAbbr[m[1].toLowerCase()]
            if (mon) return `${m[3]}-${mon}-${String(m[2]).padStart(2, '0')}`
        }
        // Fallback: try native parsing
        const d = new Date(str)
        return !isNaN(d) ? d.toISOString().split('T')[0] : null
    }

    // ===== RESERVATION NUMBER =====
    // Email: "Reservation # 44261366" / Cart: "Reservation: 44261366"
    const resMatch = text.match(/Reservation\s*#?\s*[:\s]*(\d{6,})/i) || text.match(/Stateroom\s*GTY\s*Reservation\s*[:\s]*(\d+)/i)
    data.reservationNum = resMatch ? resMatch[1] : null

    // ===== SHIP =====
    const shipMatch = text.match(/Ship\s+[:\s]*(Disney\s+\w+)/i)
    data.ship = shipMatch ? shipMatch[1].trim() : null

    // ===== CRUISE NAME / ITINERARY =====
    // Email (multi-line after position-aware extraction):
    //   "Cruise Itinerary 4-Night Halloween on the High\nSeas Bahamian Cruise from Port\nCanaveral"
    // Cart: "Cruise Itinerary 3-Night Bahamian Cruise from Port Canaveral"
    // Use [\s\S] to span newlines in position-aware extracted text
    const cruiseMatch = text.match(/Cruise\s*Itinerary\s+(\d+-Night[\s\S]+?Cruise[\s\S]+?)(?:\s+(?:Embark|Depart|Check-in))/i) ||
                        text.match(/(\d+-Night\s+\w+\s+Cruise(?:\s+from\s+[\w\s]+?)?)\s+(?:Embark|Depart|Ship\s+Disney|Check-in|\d{1,2}-\w+-\d{4})/i) ||
                        text.match(/(\d+-Night\s+\w+\s+Cruise)/i)
    // Clean up newlines from multi-line capture
    data.cruiseName = cruiseMatch ? cruiseMatch[1].replace(/\s*\n\s*/g, ' ').trim() : null

    // ===== DATES =====
    if (isEmailConfirmation) {
        // Email format: "Embark Date 19-Jun-2026" / "Debark Date 22-Jun-2026"
        const embarkMatch = text.match(/Embark\s*Date\s+(\d{1,2}-\w+-\d{4})/i) || text.match(/Leaving\s+(\d{1,2}-\w+-\d{4})/i)
        const debarkMatch = text.match(/Debark\s*Date\s+(\d{1,2}-\w+-\d{4})/i) || text.match(/Returning\s+(\d{1,2}-\w+-\d{4})/i)
        data.travelDate = parseDCLDate(embarkMatch?.[1])
        data.travelEnd = parseDCLDate(debarkMatch?.[1])
    } else {
        // Cart format: "May 20 - 24, 2026"
        const datesMatch = text.match(/(\w+\s+\d+)\s*-\s*(\d+),\s*(\d{4})/i)
        if (datesMatch) {
            const startDate = new Date(`${datesMatch[1]}, ${datesMatch[3]}`)
            if (!isNaN(startDate)) data.travelDate = startDate.toISOString().split('T')[0]
            const monthName = datesMatch[1].split(' ')[0]
            const endDate = new Date(`${monthName} ${datesMatch[2]}, ${datesMatch[3]}`)
            if (!isNaN(endDate)) data.travelEnd = endDate.toISOString().split('T')[0]
        }
    }

    // ===== STATEROOM / CABIN =====
    const stateroomNumMatch = text.match(/Stateroom\s+(\d{3,})/i)
    const deckMatch = text.match(/Deck\s+(\d+)/i)
    const categoryMatch = text.match(/Category\s+(\w+)/i)
    const descMatch = text.match(/Description\s+([\w\s]+Stateroom)/i)
    data.stateroomNum = stateroomNumMatch ? stateroomNumMatch[1] : null
    data.deck = deckMatch ? deckMatch[1] : null
    data.category = categoryMatch ? categoryMatch[1].trim() : null
    // Room type description (e.g. "Standard Inside Stateroom") — separate from stateroom# and deck
    data.roomDescription = descMatch?.[1]?.trim() || null
    if (!data.roomDescription) {
        const oldMatch = text.match(/(Deluxe\s+[\w\s]+Stateroom|Concierge\s+[\w\s]+|Oceanview\s+[\w\s]+)/i)
        data.roomDescription = oldMatch ? oldMatch[1].trim() : null
    }
    // Build cabinType for detalle display (combined string)
    const cabinParts = [data.roomDescription, data.stateroomNum ? `#${data.stateroomNum}` : null, data.deck ? `Deck ${data.deck}` : null].filter(Boolean)
    data.cabinType = cabinParts.length > 0 ? cabinParts.join(', ') : null

    // ===== GUESTS =====
    data.guests = []
    data.guestsDetailed = []

    if (isEmailConfirmation) {
        // DCL email format: "MS CAROLINA ACHURY MONCADA Citizenship CO" or "MSTR MARTIN REYES ACHURY Age 7 Citizenship CO"
        // Name always ends at "Citizenship" or "Age \d+" — use lookahead to stop capture and avoid grabbing boilerplate
        const guestRegex = /\b(MS|MR|MRS|MISS|MSTR|MASTER)\s+([A-ZÁÉÍÓÚÑ]+(?:\s+[A-ZÁÉÍÓÚÑ]+){1,4})\s+(?=CITIZENSHIP|AGE\s+\d)/g
        let gMatch
        while ((gMatch = guestRegex.exec(textUpper)) !== null) {
            const title = gMatch[1]
            const rawName = gMatch[2].trim()
            if (rawName.length < 4) continue
            const name = rawName.split(/\s+/).map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
            const isMiss = title === 'MISS'
            const isMstr = title === 'MSTR' || title === 'MASTER'
            const isMenor = isMiss || isMstr
            let edad = null
            if (isMenor) {
                const namePos = textUpper.indexOf(gMatch[0])
                if (namePos !== -1) {
                    const afterName = textUpper.substring(namePos, namePos + 200)
                    const ageMatch = afterName.match(/AGE\s+(\d+)/)
                    if (ageMatch) edad = ageMatch[1]
                }
            }
            data.guests.push(name)
            data.guestsDetailed.push({ nombre: name, titulo: title, esMenor: isMenor, edad })
        }

        // Fallback: if no guests found with Citizenship/Age terminator, try title + 2-3 words
        if (data.guests.length === 0) {
            const fallbackRegex = /\b(MS|MR|MRS|MISS|MSTR|MASTER)\s+([A-ZÁÉÍÓÚÑ]+(?:\s+[A-ZÁÉÍÓÚÑ]+){1,3})/g
            const legalWords = ['SHIP', 'EMBARK', 'DESIGNAT', 'RECEIPT', 'RIGHT', 'REQUIRE', 'DEBARK', 'MEDICAL', 'COMFORT', 'SAFETY', 'GUEST', 'CREW', 'CABIN', 'ROOM', 'AMENITY', 'CARD', 'BOARD', 'VESSEL', 'CARRIER', 'PASSENGER', 'TERMS', 'CONDITION']
            let fMatch
            while ((fMatch = fallbackRegex.exec(textUpper)) !== null) {
                const title = fMatch[1]
                const rawName = fMatch[2].trim()
                if (rawName.length < 4) continue
                if (legalWords.some(w => rawName.includes(w))) continue
                const name = rawName.split(/\s+/).map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
                const isMenor = title === 'MISS' || title === 'MSTR' || title === 'MASTER'
                data.guests.push(name)
                data.guestsDetailed.push({ nombre: name, titulo: title, esMenor: isMenor, edad: null })
            }
        }
    } else {
        // Cart format: "FirstName LastName (Age 18+)"
        const travelPartyMatch = text.match(/Travel\s*Party([\s\S]*?)(?:Payment\s*Information|Price\s*Summary)/i)
        if (travelPartyMatch) {
            const partyText = travelPartyMatch[1]
            const nameMatches = partyText.matchAll(/([A-Za-záéíóúñÁÉÍÓÚÑ]+)\s+([A-Za-záéíóúñÁÉÍÓÚÑ\s]+?)\s*\(Age\s*([\d+]+)\)/gi)
            for (const match of nameMatches) {
                data.guests.push({ firstName: match[1].trim(), lastName: match[2].trim(), age: match[3] })
            }
        }
    }

    // Guest counts
    const adultsCount = data.guestsDetailed.filter(g => !g.esMenor).length
    const childrenCount = data.guestsDetailed.filter(g => g.esMenor).length
    if (adultsCount > 0) {
        data.adultsCount = adultsCount
        data.childrenCount = childrenCount
    } else {
        const guestsCountMatch = text.match(/(\d+)\s*Adults?,\s*(\d+)\s*Child/i)
        if (guestsCountMatch) {
            data.adultsCount = parseInt(guestsCountMatch[1])
            data.childrenCount = parseInt(guestsCountMatch[2])
        }
    }

    // ===== PRICES =====
    // Grand Total (email) or Cruise Price (cart)
    const grandTotalMatch = text.match(/Grand\s*Total\s*\$?([\d,]+\.?\d*)/i)
    const cruisePriceMatch = text.match(/Cruise\s*Price\s*\$?([\d,]+\.?\d*)\s*USD/i)
    const stateroomGTYMatch = text.match(/Stateroom\s*GTY\s*\$?([\d,]+\.?\d*)\s*USD/i)
    data.totalVenta = grandTotalMatch ? parseFloat(grandTotalMatch[1].replace(/,/g, '')) :
                      cruisePriceMatch ? parseFloat(cruisePriceMatch[1].replace(/,/g, '')) :
                      stateroomGTYMatch ? parseFloat(stateroomGTYMatch[1].replace(/,/g, '')) : 0

    // Commission: "Commission Earned $523.94 **" (email) or "Commission $X USD" (cart)
    const commEarnedMatch = text.match(/Commission\s*Earned\s*\$?([\d,]+\.?\d*)/i)
    const commMatch = text.match(/Commission\s*\$?([\d,]+\.?\d*)\s*USD/i)
    data.commission = commEarnedMatch ? parseFloat(commEarnedMatch[1].replace(/,/g, '')) :
                      commMatch ? parseFloat(commMatch[1].replace(/,/g, '')) : 0

    // Balance Due
    const balanceDueMatch = text.match(/Balance\s*Due\s*\$?([\d,]+\.?\d*)/i)
    data.balanceDue = balanceDueMatch ? parseFloat(balanceDueMatch[1].replace(/,/g, '')) : null

    // Net Due / Net Remaining
    const netMatch = text.match(/Net\s*(?:Due|Remaining)\s*\$?([\d,]+\.?\d*)/i)
    data.netRemaining = netMatch ? parseFloat(netMatch[1].replace(/,/g, '')) : null

    // Deposit Due
    const depositMatch = text.match(/Deposit\s*Due\s*\$?([\d,]+\.?\d*)/i)
    data.depositDue = depositMatch ? parseFloat(depositMatch[1].replace(/,/g, '')) : null

    // ===== PAYMENT DEADLINES =====
    if (isEmailConfirmation) {
        // "Final Payment Due Date 21-Mar-2026 11:59 PM US Eastern time"
        const finalPaymentMatch = text.match(/Final\s*Payment\s*Due\s*Date\s+(\d{1,2}-\w+-\d{4})/i) ||
                                   text.match(/Final\s*Payment\s*Due\s*Date\s+(\w+\s+\d+,?\s*\d{4})/i)
        data.balanceDeadline = parseDCLDate(finalPaymentMatch?.[1])
    } else {
        const depositDeadlineMatch = text.match(/Due\s*([\w]+\s+\d+,\s*\d{4})/i)
        if (depositDeadlineMatch) {
            const deadline = new Date(depositDeadlineMatch[1])
            if (!isNaN(deadline)) data.depositDeadline = deadline.toISOString().split('T')[0]
        }
        const balanceDeadlineMatch = text.match(/Due\s*([\w]+\s+\d+,\s*\d{4})[\s\S]*?Remaining\s*Balance/i) ||
                                      text.match(/Remaining\s*Balance[\s\S]*?Due\s*([\w]+\s+\d+,\s*\d{4})/i)
        if (balanceDeadlineMatch) {
            const deadline = new Date(balanceDeadlineMatch[1])
            if (!isNaN(deadline)) data.balanceDeadline = deadline.toISOString().split('T')[0]
        }
    }

    // ===== EMBARKATION PORT =====
    // Email: first port in itinerary section "Port PORT CANAVERAL"
    const portMatch = text.match(/Port\s+(PORT\s+\w+)/i) || text.match(/Departs\s*[:\s]*([\w\s,]+?)(?:\s+Ports|\s+Ship|$)/i)
    data.embarkationPort = portMatch ? portMatch[1].trim().replace(/^PORT\s+/i, '') : null

    // ===== PAYMENT STATUS =====
    const paidMatch = text.match(/Paid\s*\$?([\d,]+\.?\d*)\s*USD/i)
    if (paidMatch) {
        const paidAmount = parseFloat(paidMatch[1].replace(/,/g, ''))
        if (paidAmount >= data.totalVenta && data.totalVenta > 0) {
            data.paymentStatus = 'paid'; data.stage = 'pago_completo'; data.abonado = paidAmount
        } else if (paidAmount > 0) {
            data.paymentStatus = 'partial'; data.stage = 'reserva_abono'; data.abonado = paidAmount
        }
    } else if (data.balanceDue != null && data.balanceDue < data.totalVenta && data.totalVenta > 0) {
        // Partial payment: balance < total means deposit was paid
        const depositPaid = data.totalVenta - data.balanceDue
        data.paymentStatus = 'partial'; data.stage = 'reserva_abono'; data.abonado = depositPaid
    } else if (data.netRemaining === 0) {
        data.paymentStatus = 'paid'; data.stage = 'pago_completo'; data.abonado = data.totalVenta
    } else if (data.depositDue) {
        data.paymentStatus = 'pending'; data.stage = 'prereserva'; data.abonado = 0
    } else if (isEmailConfirmation && data.totalVenta > 0) {
        // "Reservation is confirmed" + no deposit due = deposit was paid
        // Amount paid = total - balance (or total if fully paid)
        const paid = data.balanceDue != null ? data.totalVenta - data.balanceDue : data.totalVenta
        if (paid >= data.totalVenta) {
            data.paymentStatus = 'paid'; data.stage = 'pago_completo'; data.abonado = data.totalVenta
        } else if (paid > 0) {
            data.paymentStatus = 'partial'; data.stage = 'reserva_abono'; data.abonado = paid
        } else {
            data.paymentStatus = 'confirmed'; data.stage = 'prereserva'; data.abonado = 0
        }
    } else {
        data.paymentStatus = 'pending'; data.stage = 'prereserva'
    }

    // ===== PRIMARY GUEST (TITULAR) =====
    if (data.guestsDetailed && data.guestsDetailed.length > 0) {
        // First adult guest
        const primary = data.guestsDetailed.find(g => !g.esMenor) || data.guestsDetailed[0]
        data.titular = primary.nombre
    } else if (data.guests.length > 0) {
        const primary = data.guests.find(g => g.age === '18+') || data.guests[0]
        data.titular = window.normalizeName(typeof primary === 'string' ? primary : `${primary.firstName} ${primary.lastName}`)
    }

    // ===== DESTINATION =====
    data.destination = data.ship || 'Disney Cruise Line'
    if (data.cruiseName) data.destination = data.cruiseName

    // ===== DETALLE (human-readable summary) =====
    const detalleParts = [data.ship, data.cruiseName, data.embarkationPort ? `desde ${data.embarkationPort}` : null,
        data.cabinType].filter(Boolean)
    if (detalleParts.length > 0) data.detalle = detalleParts.join(' — ')

    // Commission M2M (configurable)
    if (data.commission) {
        data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))
    }

    // Abono esperado: use actual deposit from PDF if available, otherwise formula
    if (data.abonado > 0) {
        data.abono = data.abonado  // PDF deposit paid = expected deposit (trust the PDF)
    } else if (data.depositDue > 0) {
        data.abono = data.depositDue  // PDF says deposit amount due
    } else {
        data.abono = parseFloat((data.totalVenta * (settings.abonoCrucero || 25) / 100).toFixed(2))
    }

    // Fechas límite
    data.fechaAbono = data.depositDeadline || null
    data.fechaPagoFinal = data.balanceDeadline || null

    return data
}

// ========== EXPEDIA TAAP PARSER ==========
function parseExpediaTAAP(text) {
    const { settings } = store
    const textUpper = text.toUpperCase()

    // Detect activity/ticket vs hotel format
    const isActivity = textUpper.includes('THINGS TO DO') || textUpper.includes('ACTIVITY IN') ||
                       textUpper.includes('ADULT TICKETS') || textUpper.includes('ACTIVITY TOTAL') ||
                       textUpper.includes('THEME PARK TICKETS') || textUpper.includes('TOUR DATE')

    if (isActivity) {
        return parseExpediaTAAPActivity(text, textUpper, settings)
    }

    const data = {
        operator: 'Expedia TAAP',
        operatorType: 'expedia',
        type: 'hotel'
    }

    // Helper: parse a date string like "Mon, Mar 30, 2026" or "Mar 30" (adds year)
    function _parseTAAPDate(str, fallbackYear) {
        if (!str) return null
        let s = str.trim()
        if (!s.match(/\d{4}/)) s += ', ' + (fallbackYear || new Date().getFullYear())
        const d = new Date(s)
        return isNaN(d) ? null : d.toISOString().split('T')[0]
    }

    // Itinerary Number (EN: "Itinerary number:", ES: "Número de itinerario:")
    const itinMatch = text.match(/(?:Itinerary\s*number|Número\s*de\s*itinerario)\s*[:\s]*([\d]+)/i)
    data.reservationNum = itinMatch ? itinMatch[1] : null

    // Confirmation Number
    const confMatch = text.match(/Confirmation\s*[:\s]*([\w\d]+)/i)
    data.confirmationNum = confMatch ? confMatch[1] : null

    // Primary Traveler — multiple strategies for two-column PDF layouts
    // Strategy 1: "Room 1: Name • 2 adults" or "Habitación 1: Name • 2 adultos"
    // Strategy 2: name on line AFTER "Primary traveler" / "Viajero principal"
    // Strategy 3: name on same line after header
    const travelerMatch =
        text.match(/(?:Room|Habitaci[oó]n)\s*\d+\s*:\s*\n?\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)(?:\s*[•·]\s*\d|\s*\n)/i) ||
        text.match(/(?:Primary\s*traveler|Viajero\s*principal).*\n\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)(?:\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|lun|mar|mi[eé]|jue|vie|s[aá]b|dom),)/i) ||
        text.match(/(?:Primary\s*traveler|Viajero\s*principal)\s*\n\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)\s*\n/i)
    data.titular = travelerMatch ? window.normalizeName(travelerMatch[1].trim()) : null

    // Hotel Name — "Stay at [name]" (appears in both EN and ES PDFs)
    const stayMatch = text.match(/Stay\s+at\s+(.+?)(?:\s+Price\s+details|\s+Detalles\s+del\s+precio|\s*\n|\s*Primary|\s*Viajero)/i)
    if (stayMatch) {
        data.hotel = stayMatch[1].trim()
    }
    // Fallback: hotel name line followed by address (standalone line in property section)
    if (!data.hotel) {
        const hotelAddrMatch = text.match(/^([A-Za-z0-9&''\-\s]{10,}(?:Inn|Suites|Hotel|Resort|Lodge|Bay|Vista|Beach|Springs|Garden|Courtyard|Hampton|Hilton|Marriott|Hyatt|Fairfield)[\w\s&']*?)\s*\n\s*\d+/m)
        data.hotel = hotelAddrMatch ? hotelAddrMatch[1].trim() : null
    }

    // Dates — multiple strategies for two-column PDF layout
    // Strategy 1: Find ALL full dates with year (Mon, Mar 30, 2026) in the text.
    const allFullDates = [...text.matchAll(/(\w{3},\s*\w+\s+\d+,\s*\d{4})/gi)]
    if (allFullDates.length >= 2) {
        data.travelDate = _parseTAAPDate(allFullDates[0][1])
        data.travelEnd = _parseTAAPDate(allFullDates[1][1])
    } else if (allFullDates.length === 1) {
        data.travelDate = _parseTAAPDate(allFullDates[0][1])
    }
    // Strategy 2: "Mon, Mar 30 - Fri, Apr 03" inline date range (without year)
    if (!data.travelDate) {
        const travelDatesInline = text.match(/(\w{3},\s*\w+\s+\d+)\s*-\s*(\w{3},\s*\w+\s+\d+)/i)
        if (travelDatesInline) {
            const year = new Date().getFullYear()
            data.travelDate = _parseTAAPDate(travelDatesInline[1], year)
            data.travelEnd = _parseTAAPDate(travelDatesInline[2], year)
        }
    }
    // Strategy 3: Spanish dates — "vie, 01 de may de 2026" or "01 de may de 2026"
    if (!data.travelDate) {
        const esMonths = { ene: 'Jan', feb: 'Feb', mar: 'Mar', abr: 'Apr', may: 'May', jun: 'Jun', jul: 'Jul', ago: 'Aug', sep: 'Sep', oct: 'Oct', nov: 'Nov', dic: 'Dec' }
        const esDates = [...text.matchAll(/(?:\w{3},?\s*)?(\d{1,2})\s+de\s+(\w{3})\w*\s+de\s+(\d{4})/gi)]
        if (esDates.length >= 2) {
            const d1 = esDates[0], d2 = esDates[1]
            const m1 = esMonths[d1[2].toLowerCase().substring(0, 3)], m2 = esMonths[d2[2].toLowerCase().substring(0, 3)]
            if (m1) data.travelDate = _parseTAAPDate(`${m1} ${d1[1]}, ${d1[3]}`)
            if (m2) data.travelEnd = _parseTAAPDate(`${m2} ${d2[1]}, ${d2[3]}`)
        }
    }
    // Strategy 4: Spanish inline range — "vie 01 de may - mar 05 de may" (no year)
    if (!data.travelDate) {
        const esMonths = { ene: 'Jan', feb: 'Feb', mar: 'Mar', abr: 'Apr', may: 'May', jun: 'Jun', jul: 'Jul', ago: 'Aug', sep: 'Sep', oct: 'Oct', nov: 'Nov', dic: 'Dec' }
        const esRange = text.match(/(\w{3})\s+(\d{1,2})\s+de\s+(\w{3})\w*\s*-\s*(\w{3})\s+(\d{1,2})\s+de\s+(\w{3})/i)
        if (esRange) {
            const year = new Date().getFullYear()
            const m1 = esMonths[esRange[3].toLowerCase().substring(0, 3)], m2 = esMonths[esRange[6].toLowerCase().substring(0, 3)]
            if (m1) data.travelDate = _parseTAAPDate(`${m1} ${esRange[2]}, ${year}`)
            if (m2) data.travelEnd = _parseTAAPDate(`${m2} ${esRange[5]}, ${year}`)
        }
    }

    // Room details — "Room, 2 Queen Beds" or Spanish "Habitación empresarial, 2 camas Queen"
    const roomMatch = text.match(/(?:Room|Habitaci[oó]n[\w\s]*),\s*(.+?)(?:\s*\n|\s*Free|\s*\d+\s*(?:nights|noches))/i)
    data.roomType = roomMatch ? roomMatch[0].split('\n')[0].trim() : null

    // Nights (EN: "4 nights", ES: "4 noches")
    const nightsMatch = text.match(/(\d+)\s*(?:nights?|noches?)/i)
    data.nights = nightsMatch ? parseInt(nightsMatch[1]) : null

    // Guests — "2 adults, 2 children" or "2 adultos, 2 niños"
    const guestsMatch = text.match(/(\d+)\s*(?:adults?|adultos?),\s*(\d+)\s*(?:children?|ni[ñn]os?)/i)
    if (guestsMatch) {
        data.adultsCount = parseInt(guestsMatch[1])
        data.childrenCount = parseInt(guestsMatch[2])
    } else {
        const adultsOnly = text.match(/(\d+)\s*(?:adults?|adultos?)\b/i)
        if (adultsOnly) {
            data.adultsCount = parseInt(adultsOnly[1])
            data.childrenCount = 0
        }
    }

    // Total Price — handle "Total US$478.37" and "Total $478.37" and "Total\n$478.37"
    const totalMatch = text.match(/Total[:\s]+(?:US)?\$?([\d,]+\.?\d*)/i)
    data.totalVenta = totalMatch ? parseFloat(totalMatch[1].replace(',', '')) : 0

    // Room price (EN: "Room", ES: "Habitación")
    const roomPriceMatch = text.match(/(?:Room|Habitaci[oó]n)\s+(?:US)?\$?([\d,]+\.?\d*)/i)
    data.roomPrice = roomPriceMatch ? parseFloat(roomPriceMatch[1].replace(',', '')) : null

    // Taxes (EN: "Taxes and Fees", ES: "Impuestos y cargos")
    const taxesMatch = text.match(/(?:Taxes\s*(?:and\s*Fees)?|Impuestos\s*y\s*cargos)\s+(?:US)?\$?([\d,]+\.?\d*)/i)
    data.taxes = taxesMatch ? parseFloat(taxesMatch[1].replace(',', '')) : 0

    // Payment status (EN: "Paid to Expedia", ES: "Pagado a Expedia")
    const hasPaidToExpedia = /(?:Paid|Pagado)\s*(?:to|a)\s*Expedia/i.test(text)
    if (hasPaidToExpedia || textUpper.includes('UPCOMING') || textUpper.includes('NO NEED TO RECONFIRM') ||
        textUpper.includes('PRÓXIMOS') || textUpper.includes('NO ES NECESARIO VOLVER A CONFIRMAR')) {
        data.paymentStatus = 'paid'
        data.stage = 'pago_completo'
        data.abonado = data.totalVenta
    } else {
        data.paymentStatus = 'pending'
        data.stage = 'prereserva'
    }

    // Destination
    data.destination = data.hotel || 'Hotel via Expedia TAAP'

    // Commission (estimate 10% for hotels)
    data.commission = parseFloat((data.totalVenta * 0.10).toFixed(2))
    data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))

    // Abono (configurable para hoteles)
    data.abono = settings.abonoHotel || 100

    return data
}

// ========== EXPEDIA TAAP ACTIVITY/TICKET PARSER ==========
function parseExpediaTAAPActivity(text, textUpper, settings) {
    const data = {
        operator: 'Expedia TAAP',
        operatorType: 'expedia'
    }

    // Helper: parse a date string
    function _parseTAAPDate(str, fallbackYear) {
        if (!str) return null
        let s = str.trim()
        if (!s.match(/\d{4}/)) s += ', ' + (fallbackYear || new Date().getFullYear())
        const d = new Date(s)
        return isNaN(d) ? null : d.toISOString().split('T')[0]
    }

    // Detect park brand
    if (textUpper.includes('WALT DISNEY WORLD') || textUpper.includes('DISNEY')) {
        data.type = 'disney'
        data.tipoParque = 'Disney World'
    } else if (textUpper.includes('UNIVERSAL')) {
        data.type = 'universal'
        data.tipoParque = 'Universal Orlando'
    } else {
        data.type = 'disney'
        data.tipoParque = 'Disney World'
    }

    // Itinerary Number
    const itinMatch = text.match(/Itinerary\s*number\s*[:\s]*([\d]+)/i) ||
                      text.match(/TAAP\s*Itinerary\s*#?\s*[:\s]*([\d]+)/i)
    data.reservationNum = itinMatch ? itinMatch[1] : null

    // Confirmation / Supplier Reference
    const confMatch = text.match(/Confirmation\s*[:\s]*([\w\d]+)/i) ||
                      text.match(/Supplier\s*Reference\s*(?:Number|#)?\s*[:\s]*([\w\d]+)/i) ||
                      text.match(/Voucher\s*#?\s*[:\s]*([\w\d]+)/i)
    data.confirmationNum = confMatch ? confMatch[1] : null

    // Primary Traveler — same strategies as hotel parser
    // Strategy 1: "Tickets: 2 adult tickets\nAndres Sanchez" — activity-specific
    // Strategy 2: name on line after "Primary traveler", before day abbreviation
    // Strategy 3: name on next line after "Primary traveler"
    // Strategy 4: "Lead Traveler:\nAndres Sanchez (Adult 1/2)" — voucher format
    const travelerMatch =
        text.match(/Customer\s*Name\s*[:\s]*\n?\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)(?:\s*\n|\s*$)/i) ||
        text.match(/Reserved\s*for\s*\n?\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)(?:\s*\n|\s*\d)/i) ||
        text.match(/Tickets\s*[:\s]*\d+\s*adult\s*tickets?\s*\n\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)\s*\n/i) ||
        text.match(/Primary\s*traveler.*\n\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)(?:\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),)/i) ||
        text.match(/Primary\s*traveler\s*\n\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)\s*\n/i) ||
        text.match(/Lead\s*Traveler\s*[:\s]*\n?\s*([A-Za-zÁÉÍÓÚÑáéíóúñ][A-Za-zÁÉÍÓÚÑáéíóúñ\s]+?)(?:\s*\(|\s*\n)/i)
    data.titular = travelerMatch ? window.normalizeName(travelerMatch[1].trim()) : null

    // Ticket info from title — "Theme Park Tickets: 3-Day, 3-Day, Theme Park Base Ticket"
    const ticketTitleMatch = text.match(/Theme\s*Park\s*Tickets?\s*[:\s]*(.+?)(?:\s*\n|$)/im)
    if (ticketTitleMatch) {
        const titleParts = ticketTitleMatch[1].trim()
        const daysMatch = titleParts.match(/(\d+)-Day/i)
        data.diasParque = daysMatch ? parseInt(daysMatch[1]) : null
        if (titleParts.toUpperCase().includes('PARK HOPPER')) {
            data.tickets = 'Park Hopper'
            data.parkHopper = true
        } else if (titleParts.toUpperCase().includes('BASE')) {
            data.tickets = 'Base Ticket'
            data.parkHopper = false
        } else {
            data.tickets = titleParts
        }
    }

    // Express Pass detection — separate from park admission tickets
    const expressPassMatch = text.match(/Express\s*Pass(?:es)?\s*[:\s]*(\d+)-Day/i) ||
                             text.match(/(\d+)-Day[,\s]*(?:Universal\s+)?[\w\s]*Express\s*Pass/i)
    if (expressPassMatch) {
        data.isExpressPass = true
        data.expressPassDays = parseInt(expressPassMatch[1]) || 1
        // Extract which park(s) the express pass covers
        const parkMatch = text.match(/Express\s*Pass[^]*?(Universal\s+Studios\s+Florida|Islands?\s+of\s+Adventure|Epic\s+Universe|Volcano\s+Bay)/i)
        data.expressPassPark = parkMatch ? parkMatch[1].trim() : 'Universal Orlando'
        data.detalle = `Express Pass ${data.expressPassDays}-Day — ${data.expressPassPark}`
        // Express passes are NOT park admission — don't set diasParque
        data.diasParque = null
        data.tickets = null
    }

    // Voucher count (Express Pass PDFs show "2 Vouchers" = number of travelers)
    const voucherCountMatch = text.match(/(\d+)\s*Vouchers?/i)
    if (voucherCountMatch && !data.adultsCount) {
        data.adultsCount = parseInt(voucherCountMatch[1]) || 1
    }


    // Tour duration fallback
    if (!data.diasParque) {
        const durationMatch = text.match(/Tour\s*duration\s*[:\s]*(\d+)\s*d/i)
        data.diasParque = durationMatch ? parseInt(durationMatch[1]) : null
    }

    // Ticket count — "2 adult tickets"
    const ticketCountMatch = text.match(/(\d+)\s*adult\s*tickets?/i) ||
                             text.match(/Valid\s*for\s*(\d+)\s*Adults?/i)
    if (ticketCountMatch) {
        data.adultsCount = parseInt(ticketCountMatch[1])
    } else if (!data.adultsCount) {
        data.adultsCount = 1
    }

    const childTicketMatch = text.match(/(\d+)\s*child(?:ren)?\s*tickets?/i)
    data.childrenCount = childTicketMatch ? parseInt(childTicketMatch[1]) : 0

    // Viajeros string
    data.viajeros = `${data.adultsCount || 1} adulto${(data.adultsCount || 1) > 1 ? 's' : ''}${data.childrenCount ? ` + ${data.childrenCount} menor${data.childrenCount > 1 ? 'es' : ''}` : ''}`


    // Travel dates — find "Day, Mon DD - Day, Mon DD" pattern anywhere in text
    // Two-column layout merges dates onto name line: "Andres Sanchez Mon, May 11 - Thu, May 14"
    const travelDatesInline = text.match(/(\w{3},\s*\w+\s+\d+)\s*-\s*(\w{3},\s*\w+\s+\d+)/i)
    if (travelDatesInline) {
        const year = new Date().getFullYear()
        data.travelDate = _parseTAAPDate(travelDatesInline[1], year)
        data.travelEnd = _parseTAAPDate(travelDatesInline[2], year)
        data.ticketValidFrom = data.travelDate
        data.ticketValidTo = data.travelEnd
    }
    // Fallback: full date with year "Mon, May 11, 2026"
    if (!data.travelDate) {
        const fullDateMatch = text.match(/(\w{3},\s*\w+\s+\d+,\s*\d{4})/i)
        if (fullDateMatch) data.travelDate = _parseTAAPDate(fullDateMatch[1])
        data.ticketValidFrom = data.travelDate
    }
    // Ticket valid window: "valid for any 3 days from Mon, May 11 to Fri, May 15"
    const validMatch = text.match(/valid\s+for\s+any\s+\d+\s+days?\s+from\s+\w+,\s*(\w+\s+\d+)\s+to\s+\w+,\s*(\w+\s+\d+)/i)
    if (validMatch) {
        const year = data.travelDate ? data.travelDate.split('-')[0] : new Date().getFullYear()
        if (!data.ticketValidFrom) data.ticketValidFrom = _parseTAAPDate(validMatch[1], year)
        if (!data.ticketValidTo) data.ticketValidTo = _parseTAAPDate(validMatch[2], year)
        if (!data.travelEnd) data.travelEnd = data.ticketValidTo
    }
    // Fallback: "Valid Dates: M/D/YYYY - M/D/YYYY" (voucher format)
    if (!data.travelDate) {
        const validDatesMatch = text.match(/Valid\s*Dates?\s*[:\s]*(\d{1,2}\/\d{1,2}\/\d{4})\s*-\s*(\d{1,2}\/\d{1,2}\/\d{4})/i)
        if (validDatesMatch) {
            data.travelDate = new Date(validDatesMatch[1]).toISOString().split('T')[0]
            data.travelEnd = new Date(validDatesMatch[2]).toISOString().split('T')[0]
        }
    }

    // Total Price — "Activity total $1,081.44" or "Total: $713.54" or "Total $1,081.44"
    const actTotalMatch = text.match(/Activity\s*total\s+\$?([\d,]+\.?\d*)/i) ||
                          text.match(/Total[:\s]+\$?([\d,]+\.?\d*)/i)
    data.totalVenta = actTotalMatch ? parseFloat(actTotalMatch[1].replace(',', '')) : 0

    // Payment status
    if (textUpper.includes('UPCOMING') || textUpper.includes('NO NEED TO RECONFIRM') || textUpper.includes('NON-REFUNDABLE') || textUpper.includes('NONREFUNDABLE')) {
        data.paymentStatus = 'paid'
        data.stage = 'pago_completo'
        data.abonado = data.totalVenta
    } else {
        data.paymentStatus = 'pending'
        data.stage = 'prereserva'
    }

    // Destination
    data.destination = data.tipoParque || 'Parques via Expedia TAAP'

    // Commission (estimate 10%)
    data.commission = parseFloat((data.totalVenta * 0.10).toFixed(2))
    data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))

    data.abono = settings.abonoParques || 100

    return data
}

// ========== UNIVERSAL ORLANDO / VAX PARSER ==========
function parseUniversalVAX(text) {
    const { settings } = store

    const data = {
        operator: 'Universal Orlando',
        operatorType: 'universal',
        type: 'universal',
        tipoParque: 'Universal Orlando'
    }

    // VAX date format: DDMMMYY (20FEB26, 16AUG26)
    const vaxMonths = {
        'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04', 'MAY': '05', 'JUN': '06',
        'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
    }
    function parseVAXDate(str) {
        if (!str) return null
        const m = str.match(/(\d{1,2})([A-Z]{3})(\d{2})/i)
        if (!m) return null
        const day = String(parseInt(m[1])).padStart(2, '0')
        const month = vaxMonths[m[2].toUpperCase()]
        if (!month) return null
        const year = '20' + m[3]
        return `${year}-${month}-${day}`
    }

    // Reservation Number (alphanumeric) — bold formatting in PDF causes each character
    // to be extracted twice (e.g. "JJ88NN990044UU22" for "J8N904U2").
    // Fix: grab to newline, strip non-alphanumeric, then de-duplicate consecutive chars.
    const resLineMatch = text.match(/Reservation\s*Number\s*[:\s]*([^\n]+)/i)
    if (resLineMatch) {
        const raw = resLineMatch[1].replace(/[^A-Z0-9]/gi, '')
        // De-duplicate consecutive identical characters (bold text artifact)
        const deduped = raw.replace(/(.)\1/g, '$1')
        data.reservationNum = deduped.length >= 4 ? deduped : (raw.length >= 4 ? raw : null)
    } else {
        data.reservationNum = null
    }

    // Passengers — "Passengers: KAREN MILLAN, JHON ARBOLEDA, SANTIAGO ARBOLEDA"
    const passMatch = text.match(/Passengers?\s*[:\s]*([A-ZÁÉÍÓÚÑ\s,]+?)(?:\n|Pay\s)/i)
    data.guests = []
    data.guestsDetailed = []
    if (passMatch) {
        const names = passMatch[1].split(',').map(n => n.trim()).filter(n => n.length > 2)
        for (const name of names) {
            const formatted = window.normalizeName(name)
            data.guests.push(formatted)
            data.guestsDetailed.push({ nombre: formatted, esMenor: false })
        }
    }
    data.titular = data.guests.length > 0 ? data.guests[0] : null

    // Hotel — "Hotel - check in DDMMMYY and check out DDMMMYY"
    const hotelDateMatch = text.match(/Hotel\s*-\s*check\s*in\s*(\d{1,2}[A-Z]{3}\d{2})\s*and\s*check\s*out\s*(\d{1,2}[A-Z]{3}\d{2})/i)
    if (hotelDateMatch) {
        data.travelDate = parseVAXDate(hotelDateMatch[1])
        data.travelEnd = parseVAXDate(hotelDateMatch[2])
    }

    // Hotel name — look for known Universal resort patterns
    // Use capture group [1], NOT [0], to avoid including prefix text like "check out 22AUG26"
    const hotelNameMatch = text.match(/\n\s*(UNIVERSAL['\s]+[A-Z\s]+(?:RESORT|HOTEL))/i)
        || text.match(/(UNIVERSAL'?S?\s+CABANA\s+BAY\s+BEACH\s+RESORT)/i)
        || text.match(/(UNIVERSAL'?S?\s+AVENTURA\s+HOTEL)/i)
        || text.match(/(UNIVERSAL'?S?\s+ENDLESS\s+SUMMER\s+RESORT[A-Z\s]*)/i)
        || text.match(/(UNIVERSAL'?S?\s+DOCKSIDE\s+INN[A-Z\s]*)/i)
        || text.match(/(LOEWS\s+(?:PORTOFINO\s+BAY|ROYAL\s+PACIFIC|SAPPHIRE\s+FALLS)\s+(?:HOTEL|RESORT)[A-Z\s]*)/i)
        || text.match(/(HARD\s+ROCK\s+HOTEL[A-Z\s]*)/i)
    if (hotelNameMatch) {
        // Title Case the hotel name from capture group
        data.hotel = hotelNameMatch[1].trim().split(/\s+/).map(w =>
            w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
        ).join(' ')
        // Fix common words that should stay lowercase or special case
        data.hotel = data.hotel.replace(/\bOf\b/g, 'of').replace(/\bAt\b/g, 'at').replace(/\bThe\b/g, 'the')
        // "Universal's" not "Universal'S"
        data.hotel = data.hotel.replace(/'S\b/g, "'s").replace(/'s\b/g, "'s")
    }

    // Room — "Room 1 - 2 Adults 1 Child" and "Room Type - Standard 2 Queen Beds"
    const roomGuestsMatch = text.match(/Room\s*\d+\s*-\s*(\d+)\s*Adults?\s*(\d+)\s*Child/i)
    if (roomGuestsMatch) {
        data.adultsCount = parseInt(roomGuestsMatch[1])
        data.childrenCount = parseInt(roomGuestsMatch[2])
    } else {
        const adultsOnly = text.match(/Room\s*\d+\s*-\s*(\d+)\s*Adults?/i)
        if (adultsOnly) data.adultsCount = parseInt(adultsOnly[1])
    }

    const roomTypeMatch = text.match(/Room\s*Type\s*-\s*(.+?)(?:\n|$)/i)
    data.roomType = roomTypeMatch ? roomTypeMatch[1].trim() : null

    // Tickets / Add-ons — capture ticket descriptions
    const ticketPatterns = [
        /(\d+-Day\s+(?:Park-to-Park|Base)\s+(?:PLUS\s+)?(?:\d+-Day\s+)?[A-Za-z\s]+Ticket)/i,
        /(\d+-Day\s+[A-Za-z\s]+(?:Ticket|Pass))/i
    ]
    let ticketDescs = []
    for (const pat of ticketPatterns) {
        const m = text.match(pat)
        if (m) { ticketDescs.push(m[1].trim()); break }
    }

    // Check for Park-to-Park
    data.parkTopark = /Park-to-Park/i.test(text)

    // Check for Epic Universe
    data.hasEpicUniverse = /Epic\s*Universe/i.test(text)

    // Check for Express Pass
    data.hasExpressPass = /Express\s*Pass/i.test(text)

    // Early Park Admission
    data.hasEarlyParkAdmission = /Early\s*Park\s*Admission/i.test(text)

    // Ticket days
    const ticketDaysMatch = text.match(/(\d+)-Day\s+(?:Park-to-Park|Base)/i)
    if (ticketDaysMatch) {
        data.diasParque = parseInt(ticketDaysMatch[1])
    }
    // If Epic Universe is separate "PLUS 1-Day", add it
    const epicDayMatch = text.match(/PLUS\s+(\d+)-Day\s+Universal\s+Epic\s+Universe/i)
    if (epicDayMatch && data.diasParque) {
        data.diasParque += parseInt(epicDayMatch[1])
    }

    // Financial data — VAX uses "USD $X,XXX.XX" format
    function parseUSD(str) {
        if (!str) return 0
        const m = str.match(/\$\s*([\d,]+\.?\d*)/)
        return m ? parseFloat(m[1].replace(/,/g, '')) : 0
    }

    // Total Price
    const totalMatch = text.match(/Total\s*Price\s*\(includes\s*taxes\s*&\s*fees\)\s*(?:USD\s*)?\$\s*([\d,]+\.?\d*)/i)
        || text.match(/Total\s*Price[^\n]*?\$\s*([\d,]+\.?\d*)/i)
    data.totalVenta = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : 0

    // Balance Due
    const balanceMatch = text.match(/Balance\s*Due[^\n]*?USD\s*\$\s*([\d,]+\.?\d*)/i)
        || text.match(/Balance\s*Due[^\n]*?\$\s*([\d,]+\.?\d*)/i)
    data.balanceDue = balanceMatch ? parseFloat(balanceMatch[1].replace(/,/g, '')) : null

    // Total Amount of Payments (what's been paid so far)
    const paidMatch = text.match(/Total\s*Amount\s*of\s*Payments\s*(?:USD\s*)?\$\s*([\d,]+\.?\d*)/i)
    const amountPaid = paidMatch ? parseFloat(paidMatch[1].replace(/,/g, '')) : 0

    // Deposit Due
    const depositMatch = text.match(/Deposit\s*Due\s*(?:on\s*)?(\d{1,2}[A-Z]{3}\d{2})\s*(?:USD\s*)?\$\s*([\d,]+\.?\d*)/i)
    if (depositMatch) {
        data.fechaAbono = parseVAXDate(depositMatch[1])
        data.depositAmount = parseFloat(depositMatch[2].replace(/,/g, ''))
    } else {
        data.depositAmount = 0
    }

    // Full Payment Due
    const fullPayMatch = text.match(/Full\s*Payment\s*Due\s*(?:on\s*)?(\d{1,2}[A-Z]{3}\d{2})\s*(?:USD\s*)?\$\s*([\d,]+\.?\d*)/i)
    if (fullPayMatch) {
        data.fechaPagoFinal = parseVAXDate(fullPayMatch[1])
    }

    // Commission — VAX doesn't typically show commission in PDF, estimate 10%
    const commMatch = text.match(/Commission\s*\$\s*([\d,]+\.?\d*)/i)
    if (commMatch) {
        data.commission = parseFloat(commMatch[1].replace(/,/g, ''))
    } else if (data.totalVenta > 0) {
        data.commission = parseFloat((data.totalVenta * 0.10).toFixed(2))
    } else {
        data.commission = 0
    }
    data.commission70 = parseFloat((data.commission * (settings.comisionM2M || 70) / 100).toFixed(2))

    // Payment status logic
    if (amountPaid > 0 && data.balanceDue === 0) {
        data.paymentStatus = 'paid'
        data.stage = 'pago_completo'
        data.abonado = data.totalVenta
        data.abono = data.totalVenta
    } else if (amountPaid > 0 && data.balanceDue > 0) {
        data.paymentStatus = 'partial'
        data.stage = 'reserva_abono'
        data.abonado = amountPaid
        data.abono = amountPaid
    } else if (data.depositAmount > 0 && amountPaid === 0) {
        // Deposit due but not yet paid
        data.paymentStatus = 'pending'
        data.stage = 'prereserva'
        data.abonado = 0
        data.abono = data.depositAmount
    } else {
        data.paymentStatus = 'pending'
        data.stage = 'prereserva'
        data.abonado = 0
        data.abono = data.depositAmount || 150
    }

    // Mark minors in guestsDetailed using adultsCount/childrenCount
    // VAX lists passengers but doesn't indicate who's a child — last N are children
    if (data.childrenCount && data.guestsDetailed.length > 0) {
        const totalGuests = data.guestsDetailed.length
        const childStart = totalGuests - data.childrenCount
        for (let i = childStart; i < totalGuests && i >= 0; i++) {
            data.guestsDetailed[i].esMenor = true
        }
    }

    // Viajeros string
    if (data.adultsCount) {
        data.viajeros = `${data.adultsCount}A`
        if (data.childrenCount) {
            data.viajeros += `, ${data.childrenCount}M`
        }
    }

    // Destination
    data.destination = data.hotel || 'Universal Orlando'

    // Detalle
    let detalles = []
    if (ticketDescs.length > 0) detalles.push(ticketDescs[0])
    if (data.hasEpicUniverse && !ticketDescs[0]?.includes('Epic')) detalles.push('Epic Universe')
    if (data.hasExpressPass) detalles.push('Express Pass')
    if (data.hasEarlyParkAdmission) detalles.push('Early Park Admission')
    data.detalle = detalles.join(' + ') || null

    return data
}

// ========== ROYAL CARIBBEAN PARSER (placeholder) ==========
function parseRoyalCaribbean(text) {
    // Similar structure to MSC/Disney - to be implemented
    return {
        operator: 'Royal Caribbean',
        operatorType: 'royal',
        type: 'crucero',
        message: 'Parser en desarrollo. Por favor ingresa los datos manualmente.'
    }
}

// ========== HELPER FUNCTIONS ==========
function formatGuestName(name) {
    if (!name) return ''
    return name.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

function showPDFPreview(data) {
    const preview = document.getElementById('pdfPreview')

    // Operator badge
    const operatorBadges = {
        'msc': '<span class="pdf-operator-badge msc">🚢 MSC Cruises</span>',
        'disney': '<span class="pdf-operator-badge disney">🚢 Disney Cruise Line</span>',
        'disneyparks': '<span class="pdf-operator-badge disneyparks">🏰 Disney Parks</span>',
        'universal': '<span class="pdf-operator-badge universal">🎢 Universal Orlando</span>',
        'expedia': '<span class="pdf-operator-badge expedia">🏨 Expedia TAAP</span>',
        'royal': '<span class="pdf-operator-badge royal">🚢 Royal Caribbean</span>'
    }

    // Express Pass indicator
    const expressPassHtml = data.isExpressPass
        ? `<span class="pdf-operator-badge" style="background:linear-gradient(135deg,#7c3aed,#a855f7);color:white;font-size:11px;padding:3px 8px;border-radius:4px;margin-left:6px;">Express Pass</span>`
        : ''

    // Payment status badge
    const statusBadges = {
        'paid': '<span class="pdf-status-badge paid">✓ Pago Completo</span>',
        'partial': '<span class="pdf-status-badge pending">⏳ Abono Pagado</span>',
        'pending': '<span class="pdf-status-badge pending">⏳ Pendiente de Pago</span>',
        'hold': '<span class="pdf-status-badge hold">🔒 Hold/Opción</span>',
        'quote': '<span class="pdf-status-badge quote">📋 Cotización</span>'
    }

    // Guest list
    let guestsHtml = ''
    if (data.guests && data.guests.length > 0) {
        guestsHtml = `
            <div class="pdf-guests-list">
                <div style="font-size:11px;color:var(--text-3);margin-bottom:8px;">👥 Viajeros (${data.guests.length})</div>
                ${data.guests.map((g, i) => {
                    const name = typeof g === 'string' ? g : `${g.firstName} ${g.lastName}`
                    const age = typeof g === 'object' && g.age ? ` (${g.age})` : ''
                    const isPrimary = i === 0 ? ' ⭐' : ''
                    return `<div class="pdf-guest-item"><span>${formatGuestName(name)}${age}</span><span>${isPrimary}</span></div>`
                }).join('')}
            </div>
        `
    } else if (data.titular) {
        guestsHtml = `
            <div class="pdf-guests-list">
                <div style="font-size:11px;color:var(--text-3);margin-bottom:8px;">👤 Titular</div>
                <div class="pdf-guest-item"><span>${data.titular}</span><span>⭐</span></div>
            </div>
        `
    }

    // Deadlines
    let deadlinesHtml = ''
    if (data.fechaAbono || data.fechaPagoFinal || data.optionDeadline || data.holdExpires) {
        deadlinesHtml = '<div style="margin-top:12px;padding:12px;background:rgba(245,158,11,0.1);border-radius:8px;border-left:3px solid var(--yellow);">'
        deadlinesHtml += '<div style="font-size:11px;color:var(--yellow);font-weight:600;margin-bottom:8px;">📅 Fechas Límite</div>'
        if (data.holdExpires) deadlinesHtml += `<div style="font-size:12px;margin-bottom:4px;">⚠️ Hold expira: <strong>${data.holdExpires}</strong></div>`
        if (data.optionDeadline) deadlinesHtml += `<div style="font-size:12px;margin-bottom:4px;">Hold válido hasta: <strong>${data.optionDeadline}</strong></div>`
        if (data.fechaAbono) deadlinesHtml += `<div style="font-size:12px;margin-bottom:4px;">Depósito: <strong>${window.fmtDate(data.fechaAbono)}</strong></div>`
        if (data.fechaPagoFinal) deadlinesHtml += `<div style="font-size:12px;">Pago final: <strong>${window.fmtDate(data.fechaPagoFinal)}</strong></div>`
        deadlinesHtml += '</div>'
    }

    preview.innerHTML = `
        <h3>${operatorBadges[data.operatorType] || data.operator} ${statusBadges[data.paymentStatus] || ''}${expressPassHtml}</h3>

        <div class="pdf-data-grid">
            <div class="pdf-data-item">
                <div class="pdf-data-label">Confirmación</div>
                <div class="pdf-data-value blue">#${data.reservationNum || 'N/A'}</div>
            </div>
            <div class="pdf-data-item">
                <div class="pdf-data-label">Tipo</div>
                <div class="pdf-data-value">${TYPE_LABELS[data.type] || data.type}</div>
            </div>
            <div class="pdf-data-item">
                <div class="pdf-data-label">${data.hotel ? 'Hotel/Resort' : 'Destino'}</div>
                <div class="pdf-data-value">${data.hotel || data.destination || data.ship || 'N/A'}</div>
            </div>
            <div class="pdf-data-item">
                <div class="pdf-data-label">Fecha Viaje</div>
                <div class="pdf-data-value">${data.travelDate ? window.fmtDate(data.travelDate) : 'N/A'}${data.travelEnd ? ' → ' + window.fmtDate(data.travelEnd) : ''}</div>
            </div>
            <div class="pdf-data-item">
                <div class="pdf-data-label">Total Venta</div>
                <div class="pdf-data-value green">${window.fmtUSD(data.totalVenta || 0)}</div>
            </div>
            <div class="pdf-data-item">
                <div class="pdf-data-label">Comisión</div>
                <div class="pdf-data-value yellow">${window.fmtUSD(data.commission || 0)} (M2M: ${window.fmtUSD(data.commission70 || 0)})</div>
            </div>
            ${data.viajeros ? `<div class="pdf-data-item">
                <div class="pdf-data-label">Viajeros</div>
                <div class="pdf-data-value">${data.viajeros}</div>
            </div>` : ''}
            ${data.email ? `<div class="pdf-data-item">
                <div class="pdf-data-label">Email</div>
                <div class="pdf-data-value">${window.escHtml(data.email)}</div>
            </div>` : ''}
            ${data.abonado > 0 ? `<div class="pdf-data-item">
                <div class="pdf-data-label">Abonado</div>
                <div class="pdf-data-value green">${window.fmtUSD(data.abonado)}</div>
            </div>` : ''}
            ${data.roomType ? `<div class="pdf-data-item">
                <div class="pdf-data-label">Habitación</div>
                <div class="pdf-data-value">${data.roomType}</div>
            </div>` : ''}
            ${data.detalle ? `<div class="pdf-data-item" style="grid-column: span 2;">
                <div class="pdf-data-label">Incluye</div>
                <div class="pdf-data-value">${data.detalle}</div>
            </div>` : ''}
        </div>

        ${guestsHtml}
        ${deadlinesHtml}

        <div class="preview-actions">
            <button class="btn btn-secondary" onclick="cancelPDFImport()">Cancelar</button>
            <button class="btn btn-success" onclick="createLeadFromPDF()">✓ Crear Lead</button>
        </div>
    `

    preview.classList.remove('hidden')
}

function cancelPDFImport() {
    document.getElementById('pdfPreview').classList.add('hidden')
    document.getElementById('pdfFileInput').value = ''
    pdfExtractedData = null
}

// ========== DUPLICATE DETECTION (Universal) ==========
function findDuplicateLead(reservationNum) {
    if (!reservationNum) return null
    const { leads, historico } = store
    const resNum = String(reservationNum).trim()
    return [...leads, ...historico].find(l => l.reservationNum && String(l.reservationNum).trim() === resNum)
}

function findDuplicateLeadMulti(record) {
    const { leads, historico } = store
    const allLeads = [...leads, ...historico]
    const recVal = parseFloat(record.totalVenta) || 0

    // Helper: check if prices differ (different sale = not a duplicate)
    function pricesDiffer(existingLead) {
        const lVal = existingLead.totalVenta || 0
        return recVal > 0 && lVal > 0 && recVal !== lVal
    }

    // 1. Exact reservation number (always trust, even with different prices)
    if (record.reservationNum) {
        const resNum = String(record.reservationNum).trim()
        const match = allLeads.find(l => l.reservationNum && String(l.reservationNum).trim() === resNum)
        if (match) return match
    }

    const recName = window.normalizeForCompare(record.titular)
    if (!recName || recName === '-') return null

    // 2. Name + phone (skip if prices differ — same family, different bookings)
    const recPhone = (record.phone || '').replace(/\D/g, '')
    if (recPhone && recPhone.length >= 7) {
        const match = allLeads.find(l => {
            const lPhone = (l.phone || '').replace(/\D/g, '')
            return lPhone === recPhone && window.normalizeForCompare(l.titular) === recName && !pricesDiffer(l)
        })
        if (match) return match
    }

    // 3. Name + travel date within 7 days (skip if prices differ — different sale)
    if (record.travelDate) {
        const recDateStr = window.parseExcelDate(record.travelDate)
        const recDate = recDateStr ? new Date(recDateStr) : null
        if (recDate && !isNaN(recDate)) {
            const match = allLeads.find(l => {
                if (window.normalizeForCompare(l.titular) !== recName) return false
                if (!l.travelDate) return false
                const lDate = new Date(l.travelDate)
                if (isNaN(lDate)) return false
                if (Math.abs(recDate - lDate) / (1000 * 60 * 60 * 24) > 7) return false
                return !pricesDiffer(l)
            })
            if (match) return match
        }
    }

    // 4. Exact name + same type (when no phone/date to differentiate)
    // Only match if BOTH have no price (likely form submissions duplicated)
    const match = allLeads.find(l => {
        if (window.normalizeForCompare(l.titular) !== recName) return false
        if (record.type && l.type && record.type !== l.type) return false
        const recVal = record.totalVenta || 0
        const lVal = l.totalVenta || 0
        return recVal === 0 && lVal === 0
    })
    if (match) return match

    // 5. Name match where existing lead has no reservation# (form lead → PDF enrichment)
    // PDF has booking data (totalVenta > 0, reservationNum), existing lead is from form (no reservation#, no totalVenta)
    if (record.reservationNum && recVal > 0) {
        const formLead = allLeads.find(l => {
            if (window.normalizeForCompare(l.titular) !== recName) return false
            if (l.reservationNum) return false // already has a reservation = different booking
            if (l.totalVenta > 0) return false // already has pricing = different booking
            return true
        })
        if (formLead) return formLead
    }

    return null
}

function showDuplicateModal(existingLead, newData, source = 'PDF') {
    // Remove existing modal if any
    const existingModal = document.getElementById('duplicateModal')
    if (existingModal) existingModal.remove()

    // Determine if this is a name-only match (form lead without reservation number)
    const isNameMatch = !existingLead.reservationNum && newData.reservationNum
    const matchReason = isNameMatch
        ? `Lead existente con el mismo nombre <strong>"${window.escHtml(existingLead.titular)}"</strong> (llegó por formulario):`
        : `Ya existe un lead con la confirmación <strong>#${newData.reservationNum || newData.resNum}</strong>:`

    const modal = document.createElement('div')
    modal.className = 'modal-bg active'
    modal.id = 'duplicateModal'
    modal.innerHTML = `
        <div class="modal" style="max-width:560px;">
            <div class="modal-head">
                <h2 class="modal-title">${isNameMatch ? '🔗 Lead Existente Detectado' : '⚠️ Reserva Duplicada'}</h2>
                <button class="modal-close" onclick="closeDuplicateModal()">×</button>
            </div>
            <div class="modal-body">
                <p style="margin-bottom:16px;">${matchReason}</p>

                <div style="background:var(--bg-3);padding:16px 20px;border-radius:8px;margin-bottom:16px;">
                    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;font-size:13px;">
                        <div><span style="color:var(--text-3);">Titular:</span> ${window.escHtml(existingLead.titular)}</div>
                        <div><span style="color:var(--text-3);">Etapa:</span> ${STAGES.find(s => s.id === existingLead.stage)?.name || existingLead.stage}</div>
                        <div><span style="color:var(--text-3);">Total:</span> ${window.fmt(existingLead.totalVenta)}</div>
                        <div><span style="color:var(--text-3);">Fecha:</span> ${existingLead.travelDate ? window.fmtDate(existingLead.travelDate) : 'N/A'}</div>
                        <div><span style="color:var(--text-3);">Fuente:</span> ${window.escHtml(existingLead.source || 'N/A')}</div>
                        <div><span style="color:var(--text-3);">Responsable:</span> ${window.escHtml(existingLead.responsable || 'N/A')}</div>
                    </div>
                </div>

                ${isNameMatch ? `<p style="font-size:12px;color:var(--cyan);margin-bottom:12px;">💡 <strong>Enriquecer</strong> agrega los datos de reserva del PDF al lead existente sin perder notas, tareas ni historial.</p>` : ''}

                <p style="font-size:13px;color:var(--text-3);">¿Qué deseas hacer?</p>
            </div>
            <div class="modal-foot">
                <button class="btn btn-secondary" onclick="closeDuplicateModal()">Cancelar</button>
                <button class="btn btn-warning" onclick="updateExistingLead('${existingLead.id}', '${source}')">${isNameMatch ? '🔗 Enriquecer Lead' : 'Actualizar Existente'}</button>
                <button class="btn btn-secondary" onclick="createDuplicateLead('${source}')">Crear Nuevo</button>
            </div>
        </div>
    `
    document.body.appendChild(modal)
}

function closeDuplicateModal() {
    const modal = document.getElementById('duplicateModal')
    if (modal) modal.remove()
}

function updateExistingLead(leadId, source) {
    closeDuplicateModal()

    const { leads, clients } = store
    const data = source === 'PDF' ? pdfExtractedData : pendingExcelRow
    if (!data) {
        window.toast('No hay datos para actualizar', 'error')
        return
    }

    const leadIndex = leads.findIndex(l => l.id === leadId)
    if (leadIndex === -1) {
        window.toast('Lead no encontrado', 'error')
        return
    }

    window.saveState('enriquecer lead desde ' + source)

    const lead = leads[leadIndex]

    // === Core booking data - financials use "only increase" (package modifications raise prices) ===
    if (data.totalVenta && data.totalVenta > (lead.totalVenta || 0)) lead.totalVenta = data.totalVenta
    if (data.commission && data.commission > (lead.commission || 0)) lead.commission = data.commission
    if (data.commission70 && data.commission70 > (lead.commission70 || 0)) lead.commission70 = data.commission70
    if (data.travelDate && !lead.travelDate) lead.travelDate = data.travelDate
    if (data.travelEnd && !lead.travelEnd) lead.travelEnd = data.travelEnd
    if ((data.destination || data.hotel) && !lead.destination) lead.destination = data.destination || data.hotel
    if (data.fechaAbono && !lead.fechaAbono) lead.fechaAbono = data.fechaAbono
    if (data.fechaPagoFinal && !lead.fechaPagoFinal) lead.fechaPagoFinal = data.fechaPagoFinal
    if (data.viajeros && !lead.viajeros) lead.viajeros = data.viajeros
    if (data.detalle && !lead.detalle) lead.detalle = data.detalle
    if (data.reservationNum && !lead.reservationNum) lead.reservationNum = data.reservationNum
    if (data.operator && !lead.platform) lead.platform = data.operator
    if (data.abono && !lead.abono) lead.abono = data.abono
    if (data.email && !lead.email) lead.email = data.email

    // === Hotel data ===
    if (data.hotel) {
        lead.hotel = data.hotel
        if (!lead.hotels) lead.hotels = []
        // Add hotel entry if not already present
        const hotelExists = lead.hotels.some(h => h.name === data.hotel)
        if (!hotelExists) {
            lead.hotels.push({
                name: data.hotel,
                provider: data.operator || '',
                reservationNum: data.reservationNum || '',
                checkIn: data.travelDate || '',
                checkOut: data.travelEnd || '',
                rooms: data.roomType || '',
                type: data.cabinType || data.roomType || '',
                cost: '',
                notes: ''
            })
        }
    }

    // === Park entries ===
    if (data.diasParque || data.tipoParque) {
        lead.diasParque = data.diasParque || lead.diasParque
        lead.tipoParque = data.tipoParque || lead.tipoParque
        lead.tickets = data.tickets || lead.tickets
        lead.parkHopper = data.parkHopper || lead.parkHopper
        lead.ticketValidFrom = data.ticketValidFrom || lead.ticketValidFrom
        lead.ticketValidTo = data.ticketValidTo || lead.ticketValidTo
        lead.hasDiningPlan = data.hasDiningPlan || lead.hasDiningPlan

        if (!lead.parkEntries) lead.parkEntries = []
        if (lead.parkEntries.length === 0) {
            lead.parkEntries.push({
                park: data.tipoParque || '',
                days: data.diasParque || '',
                ticketType: data.tickets || '',
                validFrom: data.ticketValidFrom || '',
                validTo: data.ticketValidTo || '',
                reservationNum: data.reservationNum || '',
                cost: '',
                parkHopper: data.parkHopper || false,
                diningPlan: data.hasDiningPlan || false
            })
        }
    }

    // === Express Pass data (add-on to existing park entries) ===
    if (data.isExpressPass) {
        if (!lead.parkEntries) lead.parkEntries = []
        // Find matching Universal park entry and add express pass
        const universalEntry = lead.parkEntries.find(pe =>
            (pe.park || '').toLowerCase().includes('universal')
        )
        if (universalEntry) {
            universalEntry.expressPass = true
            universalEntry.expressPassDays = data.expressPassDays || 1
            universalEntry.expressPassPark = data.expressPassPark || ''
            universalEntry.expressPassCost = data.totalVenta || 0
            universalEntry.expressPassReservationNum = data.reservationNum || ''
        } else {
            // No existing Universal entry — store as standalone add-on in formData
            if (!lead.formData) lead.formData = {}
            lead.formData.expressPass = {
                days: data.expressPassDays || 1,
                park: data.expressPassPark || '',
                cost: data.totalVenta || 0,
                reservationNum: data.reservationNum || '',
                validFrom: data.travelDate || '',
                validTo: data.travelEnd || ''
            }
        }
        // Don't overwrite lead-level financial data for add-ons
        // Express pass cost should NOT replace the main trip totalVenta
    }

    // === Cruise data ===
    if (data.ship || (data.type === 'crucero' && data.operator)) {
        if (!lead.cruises) lead.cruises = []
        const cruiseExists = lead.cruises.some(c => c.reservationNum === data.reservationNum)
        if (!cruiseExists) {
            const cruiseBalance = (data.totalVenta || 0) - (data.abonado || 0)
            lead.cruises.push({
                line: data.operator || '',
                ship: data.ship || '',
                reservationNum: data.reservationNum || '',
                departurePort: data.embarkationPort || '',
                departureDate: data.travelDate || '',
                returnDate: data.travelEnd || '',
                cabin: data.cabinType ? `${data.cabinType} ${data.cabin || ''}`.trim() : (data.cabin || ''),
                stateroomNum: data.stateroomNum || '',
                deck: data.deck || '',
                cost: data.totalVenta || '',
                notes: data.cruiseName || '',
                titular: lead.titular || '',
                totalVenta: data.totalVenta || 0,
                commission: data.commission || 0,
                abonado: data.abonado || 0,
                balance: cruiseBalance,
                paymentDueDate: data.fechaPagoFinal || ''
            })
        }
    }

    // === Grupo de viajeros (enrich if lead doesn't have it yet) ===
    if (!lead.grupoViajeros || lead.grupoViajeros.length === 0) {
        if (data.guestsDetailed && data.guestsDetailed.length > 0) {
            lead.grupoViajeros = data.guestsDetailed.map((guest, idx) => ({
                nombre: guest.nombre,
                edad: guest.edad || null,
                esMenor: guest.esMenor || false,
                esTitular: idx === 0
            }))
        } else if (data.guests && data.guests.length > 0) {
            lead.grupoViajeros = data.guests.map((name, idx) => ({
                nombre: typeof name === 'string' ? name : `${name.firstName} ${name.lastName}`,
                edad: typeof name === 'object' ? name.age : null,
                esMenor: false,
                esTitular: idx === 0
            }))
        }
    }

    // === Persist structured data to formData JSONB ===
    if (!lead.formData || typeof lead.formData !== 'object') lead.formData = {}
    if (lead.hotels) lead.formData.hotels = lead.hotels
    if (lead.parkEntries) lead.formData.parkEntries = lead.parkEntries
    if (lead.cruises) lead.formData.cruises = lead.cruises
    if (lead.grupoViajeros) lead.formData.grupoViajeros = lead.grupoViajeros

    // === Stage: only advance, never go back (preserve Sara's work) ===
    if (data.stage) {
        const stageOrder = ['nuevo', 'formulario', 'asesoria_solicitada', 'asesoria_agendada', 'asesoria_realizada', 'proximo_ano', 'cotizacion_solicitada', 'cotizacion', 'revision_cotizacion', 'prereserva', 'reserva_abono', 'pago_completo', 'documentacion', 'en_destino', 'finalizado', 'postventa']
        const currentIndex = stageOrder.indexOf(lead.stage)
        const newIndex = stageOrder.indexOf(data.stage)
        if (newIndex > currentIndex) {
            lead.stage = data.stage
            lead.progress = window.calcProgress(data.stage)
        }
    }

    // Abonado only increases — never overwrite with a lower amount
    if (data.abonado != null && data.abonado > (lead.abonado || 0)) {
        lead.abonado = data.abonado
    }
    if (data.abono && !lead.abono) {
        lead.abono = data.abono
    }

    // Also update in client's trips
    const client = clients[lead.clientKey]
    if (client) {
        const tripIndex = client.trips.findIndex(t => t.id === leadId)
        if (tripIndex !== -1) {
            Object.assign(client.trips[tripIndex], lead)
        }
    }

    // Sync to Supabase
    window.syncLeadToSupabase(lead)

    window.renderAll()

    if (source === 'PDF') {
        cancelPDFImport()
    } else {
        pendingExcelRow = null
    }

    window.toast('✓ Lead enriquecido con datos del PDF', 'success')

    // Show pipeline and scroll to the updated lead
    window.showView('pipeline')
    setTimeout(() => {
        const leadCard = document.querySelector(`[data-id="${leadId}"]`)
        if (leadCard) {
            leadCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            leadCard.style.transition = 'box-shadow 0.3s'
            leadCard.style.boxShadow = '0 0 20px var(--cyan)'
            setTimeout(() => { leadCard.style.boxShadow = '' }, 2000)
        }
        window.openLead(leadId)
    }, 300)
}

function createDuplicateLead(source) {
    closeDuplicateModal()
    if (source === 'PDF') {
        createLeadFromPDFConfirmed(pdfExtractedData)
    } else {
        window.createLeadFromExcelConfirmed(pendingExcelRow)
    }
}

function createLeadFromPDF() {
    if (!pdfExtractedData) {
        window.toast('No hay datos para crear el lead', 'error')
        return
    }

    const data = pdfExtractedData

    // Check for duplicate by reservation number or multi-field match
    const existingLead = findDuplicateLead(data.reservationNum) || findDuplicateLeadMulti({ titular: data.titular, phone: null, travelDate: data.travelDate, totalVenta: data.totalVenta, type: data.type || 'disney' })
    if (existingLead) {
        showDuplicateModal(existingLead, data, 'PDF')
        return
    }

    // Proceed with creation
    createLeadFromPDFConfirmed(data)
}

function createLeadFromPDFConfirmed(data) {
    const { leads, clients, team } = store
    const defaultResp = team[0]?.nombre || 'Sara'

    // Guardar estado para undo
    window.saveState('crear lead desde PDF')

    // Create client key
    const titular = window.normalizeName(data.titular) || 'Sin nombre'
    const clientKey = 'name:' + titular.toLowerCase()

    // Create or update client
    if (!clients[clientKey]) {
        clients[clientKey] = {
            titular: titular,
            contacto: null,
            email: data.email || null,
            phone: null,
            referido: null,
            trips: [],
            createdAt: new Date()
        }
    } else if (data.email && !clients[clientKey].email) {
        clients[clientKey].email = data.email
    }

    // Create trip/lead
    const tripId = 'trip_' + Date.now()

    // Build grupo de viajeros with ages if available
    let grupoViajeros = null
    if (data.guestsDetailed && data.guestsDetailed.length > 0) {
        // Use detailed guest info with minor detection
        grupoViajeros = data.guestsDetailed.map((guest, idx) => ({
            nombre: guest.nombre,
            edad: guest.edad || null,
            esMenor: guest.esMenor || false,
            esTitular: idx === 0
        }))
    } else if (data.guests && data.guests.length > 0) {
        // Fallback to basic guest info
        grupoViajeros = data.guests.map((name, idx) => {
            const guest = { nombre: name }
            // If this is the last guest and we have child info, it's likely the child
            if (data.childAge && idx === data.guests.length - 1 && data.childrenCount) {
                guest.edad = data.childAge
                guest.esMenor = true
            }
            // Mark first guest as titular
            if (idx === 0) guest.esTitular = true
            return guest
        })
    }

    const trip = {
        id: tripId,
        source: `PDF ${data.operator}`,
        travelDate: data.travelDate || null,
        travelEnd: data.travelEnd || null,
        destination: data.destination || data.hotel || data.operator,
        type: data.type || 'otro',
        platform: data.operator,
        totalVenta: data.totalVenta || 0,
        commission: data.commission || 0,
        commission70: data.commission70 || 0,
        abono: data.abono || window.calcularAbonoEsperado(data.type, data.totalVenta),
        abonado: data.abonado || 0,
        fechaAbono: data.fechaAbono || null,
        fechaPagoFinal: data.fechaPagoFinal || null,
        viajeros: data.viajeros || (data.guests ? `${data.guests.length} viajeros` : null),
        grupoViajeros: grupoViajeros,
        stage: data.stage || 'prereserva',
        progress: window.calcProgress(data.stage || 'prereserva'),
        responsable: defaultResp,
        reservationNum: data.reservationNum || null,
        cabinType: data.cabinType || data.roomType || null,
        detalle: data.detalle || null,
        hotel: data.hotel || null,
        diasParque: data.diasParque || null,
        tipoParque: data.tipoParque || null,
        tickets: data.tickets || null,
        parkHopper: data.parkHopper || false,
        ticketValidFrom: data.ticketValidFrom || null,
        ticketValidTo: data.ticketValidTo || null,
        hasDiningPlan: data.hasDiningPlan || false
    }

    // Build cruises[] array for cruise-type leads
    let cruises = []
    if (data.type === 'crucero') {
        const balance = (data.totalVenta || 0) - (data.abonado || 0)
        cruises = [{
            line: data.operator || '',
            ship: data.ship || '',
            reservationNum: data.reservationNum || '',
            departurePort: data.embarkationPort || '',
            departureDate: data.travelDate || '',
            returnDate: data.travelEnd || '',
            cabin: data.roomDescription || '',
            stateroomNum: data.stateroomNum || '',
            deck: data.deck || '',
            cost: data.totalVenta || 0,
            notes: data.cruiseName || '',
            // Per-reservation financials (for multi-family super merge)
            titular: titular,
            totalVenta: data.totalVenta || 0,
            commission: data.commission || 0,
            abonado: data.abonado || 0,
            balance: balance,
            paymentDueDate: data.fechaPagoFinal || ''
        }]
    }

    clients[clientKey].trips.push(trip)

    leads.push({
        id: tripId,
        clientKey: clientKey,
        titular: titular,
        contacto: null,
        phone: null,
        email: data.email || null,
        type: trip.type,
        stage: trip.stage,
        travelDate: trip.travelDate,
        travelEnd: trip.travelEnd,
        destination: trip.destination,
        totalVenta: trip.totalVenta,
        commission: trip.commission,
        commission70: trip.commission70,
        abono: trip.abono,
        abonado: trip.abonado,
        fechaAbono: trip.fechaAbono,
        fechaPagoFinal: trip.fechaPagoFinal,
        platform: trip.platform,
        progress: trip.progress,
        responsable: trip.responsable,
        reservationNum: trip.reservationNum,
        viajeros: trip.viajeros,
        grupoViajeros: trip.grupoViajeros,
        detalle: trip.detalle,
        hotel: trip.hotel,
        cruises: cruises,
        diasParque: trip.diasParque,
        tipoParque: trip.tipoParque,
        tickets: trip.tickets,
        parkHopper: trip.parkHopper,
        ticketValidFrom: trip.ticketValidFrom,
        ticketValidTo: trip.ticketValidTo,
        hasDiningPlan: trip.hasDiningPlan,
        createdAt: new Date()
    })

    // Store structured data in formData so it persists in Supabase JSONB
    const newLead = leads[leads.length - 1]
    if (!newLead.formData) newLead.formData = {}
    if (newLead.grupoViajeros) newLead.formData.grupoViajeros = newLead.grupoViajeros
    if (newLead.cruises && newLead.cruises.length > 0) newLead.formData.cruises = newLead.cruises

    // Sync to Supabase
    window.syncClientToSupabase(clientKey, clients[clientKey])
    window.syncLeadToSupabase(newLead)

    // Render and show success
    window.renderAll()
    cancelPDFImport()

    const stageName = STAGES.find(s => s.id === trip.stage)?.name || trip.stage
    window.toast(`✓ Lead "${titular}" creado en "${stageName}"`, 'success')

    // Show pipeline and open the new lead
    window.showView('pipeline')

    // Scroll to the created lead — use robust lookup since ID may mutate after Supabase sync
    const _findAndScrollToLead = () => {
        // Try by original tripId first, then find the lead by titular (ID may have changed)
        const { leads } = store
        const lead = leads.find(l => l.id === tripId) || leads.find(l => l.titular === titular && l.reservationNum === data.reservationNum)
        if (!lead) return
        const leadCard = document.querySelector(`[data-id="${lead.id}"]`)
        if (leadCard) {
            leadCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            leadCard.style.transition = 'box-shadow 0.3s'
            leadCard.style.boxShadow = '0 0 20px var(--purple)'
            setTimeout(() => { leadCard.style.boxShadow = '' }, 2000)
        }
        window.openLead(lead.id)
    }
    setTimeout(_findAndScrollToLead, 400)
    // Retry after sync completes (in case ID changed)
    setTimeout(_findAndScrollToLead, 1500)
}

// Setup PDF drag and drop
function setupPDFUploadZone() {
    const zone = document.getElementById('pdfUploadZone')
    if (!zone) return

    zone.ondragover = e => { e.preventDefault(); zone.style.borderColor = 'var(--green)'; zone.style.background = 'rgba(107,78,255,0.1)' }
    zone.ondragleave = () => { zone.style.borderColor = 'var(--purple)'; zone.style.background = '' }
    zone.ondrop = e => {
        e.preventDefault()
        zone.style.borderColor = 'var(--purple)'
        zone.style.background = ''

        const file = e.dataTransfer.files[0]
        if (file && file.name.toLowerCase().endsWith('.pdf')) {
            // Create a fake event object
            handlePDFFile({ target: { files: [file] } })
        } else {
            window.toast('Por favor arrastra un archivo PDF', 'error')
        }
    }
}

// ========== Register all functions on window ==========
Object.assign(window, {
    handlePDFFile,
    parsePDFContent,
    parseDisneyParks,
    parseMSCCruises,
    parseDisneyCruise,
    parseExpediaTAAP,
    parseUniversalVAX,
    parseRoyalCaribbean,
    formatGuestName,
    showPDFPreview,
    cancelPDFImport,
    findDuplicateLead,
    findDuplicateLeadMulti,
    showDuplicateModal,
    closeDuplicateModal,
    updateExistingLead,
    createDuplicateLead,
    createLeadFromPDF,
    createLeadFromPDFConfirmed,
    setupPDFUploadZone
})
